import { ofetch } from 'ofetch'
import type { ProjectColor } from '@/data/projectColors'

// ─── Token management ─────────────────────────────────────────────────────────

// Guarded because this runs at module scope: anything importing the client —
// a composable, a unit test — would otherwise throw outside a browser.
const storage = typeof localStorage === 'undefined' ? null : localStorage

let _token: string | null = storage?.getItem('auth_token') ?? null

export function setToken(t: string | null) {
  _token = t
  if (t) storage?.setItem('auth_token', t)
  else storage?.removeItem('auth_token')
}

/** Current access token. Needed by EventSource, which cannot set an
 *  Authorization header and must pass the token as a query param instead. */
export function getToken(): string | null {
  return _token
}

/** Refresh the access token if it is close to expiring. Exported so a
 *  long-lived SSE subscription can top up before it opens the connection —
 *  a stream that outlives the token cannot re-auth mid-flight. */
export async function ensureFreshAuthToken(): Promise<void> {
  await ensureFreshToken()
}

function authHeaders(): Record<string, string> {
  return _token ? { Authorization: `Bearer ${_token}` } : {}
}

// ─── Base fetch with auto-refresh ────────────────────────────────────────────

async function tryRefresh(): Promise<boolean> {
  try {
    const data = await ofetch<{ accessToken: string }>('/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
    setToken(data.accessToken)
    return true
  } catch {
    setToken(null)
    return false
  }
}

// ── Proactive token-expiry check ─────────────────────────────────────────────
// Decode the JWT's `exp` claim and refresh the access token *before* making
// a request if we're within EXPIRY_BUFFER_MS of expiry. This avoids the
// benign-but-noisy 401 → refresh → retry cycle that otherwise logs an
// alarming red 4xx in the browser console on every save after a long
// idle period (e.g. the 20-60s AI Suggest wait).
const EXPIRY_BUFFER_MS = 60_000 // refresh if under 1 min left

function tokenExpiryMs(): number | null {
  if (!_token) return null
  try {
    const [, payloadB64] = _token.split('.')
    if (!payloadB64) return null
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4)
    const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

// Prevent multiple concurrent refresh calls from racing each other.
let refreshInFlight: Promise<boolean> | null = null
async function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = tryRefresh().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

async function ensureFreshToken(): Promise<void> {
  if (!_token) return
  const exp = tokenExpiryMs()
  if (exp === null) return // no exp claim → let the request happen, 401 path handles it
  if (Date.now() + EXPIRY_BUFFER_MS < exp) return // still fresh
  await refreshOnce()
}

export async function apiFetch<T>(
  url: string,
  opts: Parameters<typeof ofetch>[1] = {},
): Promise<T> {
  await ensureFreshToken()
  try {
    return await ofetch<T>(url, {
      ...opts,
      headers: { ...authHeaders(), ...(opts.headers ?? {}) },
    })
  } catch (err: any) {
    // Fallback: if we still get a 401 (edge: token revoked server-side,
    // clock skew, or no exp claim), refresh once and retry.
    if (err?.response?.status === 401) {
      const ok = await refreshOnce()
      if (ok) {
        return ofetch<T>(url, {
          ...opts,
          headers: { ...authHeaders(), ...(opts.headers ?? {}) },
        })
      }
      setToken(null)
      window.location.href = '/login'
    }
    throw err
  }
}

// ─── API surface ──────────────────────────────────────────────────────────────

export const api = {
  auth: {
    me: () => apiFetch<AuthUser>('/auth/me'),
    devLogin: (email?: string) =>
      ofetch<{ accessToken: string; user: AuthUser }>('/auth/dev-login', {
        method: 'POST',
        body: { email },
      }),
    logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
    refresh: () =>
      ofetch<{ accessToken: string }>('/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      }),
  },

  users: {
    // #71: Generic per-user preference store.
    getPreference: <T = unknown>(key: string) =>
      apiFetch<{ value: T | null }>(`/api/v1/users/me/preferences/${key}`),
    setPreference: <T>(key: string, value: T) =>
      apiFetch<{ value: T }>(`/api/v1/users/me/preferences/${key}`, { method: 'PUT', body: { value } }),
    deletePreference: (key: string) =>
      apiFetch<{ deleted: boolean }>(`/api/v1/users/me/preferences/${key}`, { method: 'DELETE' }),
    // Convenience wrappers for starred projects.
    starProject: (projectId: string) =>
      apiFetch<{ ok: boolean; projectIds?: string[]; reason?: string }>(`/api/v1/users/me/starred-projects/${projectId}`, { method: 'POST' }),
    unstarProject: (projectId: string) =>
      apiFetch<{ ok: boolean; projectIds: string[] }>(`/api/v1/users/me/starred-projects/${projectId}`, { method: 'DELETE' }),
  },

  projects: {
    list: (q: Record<string, string> = {}) =>
      apiFetch<ApiProject[]>('/api/v1/projects', { query: q }),
    get: (id: string) => apiFetch<ApiProjectDetail>(`/api/v1/projects/${id}`),
    bom: (id: string) => apiFetch<ApiBomVersion | null>(`/api/v1/projects/${id}/bom`),
    // BOM stock indicator (M5 #26): { [bomLineItemId]: { okc, atl } } — only for lines that
    // match something on a warehouse shelf by manufacturer+model.
    stockAvailability: (id: string) =>
      apiFetch<ApiStockAvailability>(`/api/v1/projects/${id}/stock-availability`),
    // W11 (docs/specs/warehouse-round2-feedback.md): return sessions closed but not yet
    // reviewed, for the BOM alert banner. Clears once every session is REVIEWED.
    pendingReturns: (id: string) =>
      apiFetch<ApiPendingReturn[]>(`/api/v1/projects/${id}/returns/pending`),
    create: (body: ApiCreateProjectBody) =>
      apiFetch<ApiProject>('/api/v1/projects', { method: 'POST', body }),
    delete: (id: string) =>
      apiFetch<void>(`/api/v1/projects/${id}`, { method: 'DELETE' }),
    update: (id: string, body: ApiUpdateProjectBody) =>
      apiFetch<ApiProject>(`/api/v1/projects/${id}`, { method: 'PATCH', body }),
    createChangeOrder: (projectId: string, body: ApiCreateChangeOrderBody) =>
      apiFetch<ApiChangeOrderDetail>(`/api/v1/projects/${projectId}/change-orders`, {
        method: 'POST',
        body,
      }),
    updateChangeOrder: (projectId: string, coId: string, body: ApiUpdateChangeOrderBody) =>
      apiFetch<ApiChangeOrderDetail>(`/api/v1/projects/${projectId}/change-orders/${coId}`, {
        method: 'PATCH',
        body,
      }),
    deleteChangeOrder: (projectId: string, coId: string) =>
      apiFetch<void>(`/api/v1/projects/${projectId}/change-orders/${coId}`, { method: 'DELETE' }),
    cancelChangeOrder: (projectId: string, coId: string) =>
      apiFetch<{
        disposition: 'DELETED' | 'VOIDED'
        coNumber: number
        removedReplacementLines: number
        restoredSourceLines: number
        deletedCreditMemos: number
        unboundFundingLinks: number
      }>(`/api/v1/projects/${projectId}/change-orders/${coId}/cancel`, { method: 'POST' }),
    listChangeRequests: (projectId: string) =>
      apiFetch<ApiChangeRequest[]>(`/api/v1/projects/${projectId}/change-requests`),
    createChangeRequest: (projectId: string, body: ApiCreateChangeRequestBody) =>
      apiFetch<ApiChangeRequest>(`/api/v1/projects/${projectId}/change-requests`, { method: 'POST', body }),
    updateChangeRequest: (projectId: string, crId: string, body: ApiUpdateChangeRequestBody) =>
      apiFetch<ApiChangeRequest>(`/api/v1/projects/${projectId}/change-requests/${crId}`, { method: 'PATCH', body }),
    cancelChangeRequest: (projectId: string, crId: string) =>
      apiFetch<ApiChangeRequest & { revertSummary: ApiChangeRequestRevertSummary }>(
        `/api/v1/projects/${projectId}/change-requests/${crId}/cancel`,
        { method: 'POST' },
      ),
    savings: (projectId: string) =>
      apiFetch<ApiSavingsEvent[]>(`/api/v1/projects/${projectId}/savings`),
    createSavings: (projectId: string, body: ApiCreateSavingsBody) =>
      apiFetch<ApiSavingsEvent>(`/api/v1/projects/${projectId}/savings`, { method: 'POST', body }),
    deleteSavings: (projectId: string, eventId: string) =>
      apiFetch<{ deleted: boolean }>(`/api/v1/projects/${projectId}/savings/${eventId}`, { method: 'DELETE' }),
    getBom: (projectId: string) =>
      apiFetch<ApiBomVersion | null>(`/api/v1/projects/${projectId}/bom`),
    createBomVersion: (projectId: string) =>
      apiFetch<ApiBomVersion>(`/api/v1/projects/${projectId}/bom`, { method: 'POST', body: {} }),
    addBomLine: (projectId: string, versionId: string, body: ApiAddBomLineBody) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/${versionId}/lines`, { method: 'POST', body }),
    updateBomLine: (projectId: string, versionId: string, lineId: string, body: Partial<ApiAddBomLineBody>) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}`, { method: 'PATCH', body }),
    reassignLineChangeRequest: (projectId: string, versionId: string, lineId: string, changeRequestId: string) =>
      apiFetch<ApiBomLineItem>(
        `/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/change-request`,
        { method: 'POST', body: { changeRequestId } },
      ),
    bomLinePoImpact: (projectId: string, versionId: string, lineId: string) =>
      apiFetch<ApiPoLinePriceImpact[]>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/po-impact`),
    deleteBomLine: (projectId: string, versionId: string, lineId: string, permanent = false, changeRequestId?: string | null) => {
      const params: string[] = []
      if (permanent) params.push('permanent=true')
      if (changeRequestId) params.push(`changeRequestId=${encodeURIComponent(changeRequestId)}`)
      const qs = params.length ? `?${params.join('&')}` : ''
      return apiFetch<void>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}${qs}`, { method: 'DELETE' })
    },
    restoreBomLine: (projectId: string, versionId: string, lineId: string) =>
      apiFetch<{ status: string }>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/restore`, { method: 'POST' }),
    splitBomLine: (
      projectId: string,
      versionId: string,
      lineId: string,
      body: {
        inStockQty: number
        inStockUnitCost: number
        inStockUnitPrice: number
        toOrderQty: number
        toOrderUnitCost: number
        toOrderUnitPrice: number
        note?: string
      },
    ) =>
      apiFetch<{ splitGroupId: string; inStockChildId: string; toOrderChildId: string }>(
        `/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/split`,
        { method: 'POST', body },
      ),
    duplicateBomLine: (projectId: string, versionId: string, lineId: string) =>
      apiFetch<{ duplicatedIds: string[]; splitGroupId: string | null }>(
        `/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/duplicate`,
        { method: 'POST' },
      ),
    bomLineAudit: (projectId: string, lineId: string) =>
      apiFetch<ApiBomAuditEntry[]>(`/api/v1/projects/${projectId}/bom/lines/${lineId}/audit`),
    approveBom: (projectId: string, versionId: string) =>
      apiFetch<ApiBomVersion>(`/api/v1/projects/${projectId}/bom/${versionId}`, { method: 'PATCH', body: { approved: true } }),
    repairPriceBaseline: (projectId: string, versionId: string) =>
      apiFetch<{ matched: number; unmatched: number; total: number }>(`/api/v1/projects/${projectId}/bom/${versionId}/repair-price-baseline`, { method: 'POST', body: {} }),
    assignBudget: (projectId: string, versionId: string, body: { budgetLineItemId: string | null; lineIds: string[]; changeRequestId?: string | null }) =>
      apiFetch<{ updated: number }>(`/api/v1/projects/${projectId}/bom/${versionId}/budgets/assign`, { method: 'POST', body }),
    combineBudgets: (projectId: string, versionId: string, body: { lineIds: string[]; description: string; room?: string | null; system?: string | null }) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/${versionId}/budgets/combine`, { method: 'POST', body }),
    swapPreview: (projectId: string, versionId: string, body: ApiBulkSwapPreviewBody) =>
      apiFetch<ApiBulkSwapPreviewResponse>(`/api/v1/projects/${projectId}/bom/${versionId}/swap-preview`, { method: 'POST', body }),
    swap: (projectId: string, versionId: string, body: ApiBulkSwapBody) =>
      apiFetch<ApiBulkSwapResponse>(`/api/v1/projects/${projectId}/bom/${versionId}/swap`, { method: 'POST', body }),
    reorderBomLines: (projectId: string, versionId: string, lineIds: string[]) =>
      apiFetch<{ reordered: number }>(`/api/v1/projects/${projectId}/bom/${versionId}/order`, { method: 'PUT', body: { lineIds } }),
    bomLineTrackedItems: (projectId: string, versionId: string, lineId: string) =>
      apiFetch<ApiBomLineTrackedItems>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/tracked-items`),
    audit: (projectId: string) =>
      apiFetch<ApiAuditEntry[]>(`/api/v1/projects/${projectId}/audit`),
    revertBomLine: (projectId: string, lineId: string, auditLogId: string) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/lines/${lineId}/revert`, { method: 'POST', body: { auditLogId } }),
    accountingCheck: (projectId: string) =>
      apiFetch<ApiAccountingCheck>(`/api/v1/projects/${projectId}/accounting-check`),
    vwActivate: (projectId: string) =>
      apiFetch<{ viewName: string; activated: boolean }>(`/api/v1/projects/${projectId}/vw/activate`, { method: 'POST' }),
    vwDeactivate: (projectId: string) =>
      apiFetch<{ viewName: string; activated: boolean }>(`/api/v1/projects/${projectId}/vw/deactivate`, { method: 'POST' }),
    vwUnmatched: (projectId: string) =>
      apiFetch<ApiVwUnmatchedDevice[]>(`/api/v1/projects/${projectId}/vw/unmatched`),
    vwAcceptMatch: (projectId: string, body: { bomLineItemId: string; vwMake: string; vwModel: string }) =>
      apiFetch<{ ok: boolean }>(`/api/v1/projects/${projectId}/vw/accept-match`, { method: 'POST', body }),
    vwAiSuggest: (projectId: string) =>
      apiFetch<ApiVwAiSuggestion[]>(`/api/v1/projects/${projectId}/vw/ai-suggest`, { method: 'POST' }),
    deleteImpact: (projectId: string, versionId: string, lineId: string) =>
      apiFetch<ApiBomLineDeleteImpact>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/delete-impact`),
    deleteBudget: (projectId: string, versionId: string, lineId: string, body: { childrenAction: 'REASSIGN_TO' | 'ORPHAN_TO_BOM'; targetBudgetLineId?: string | null; reason: string; confirmDownstream?: boolean }) =>
      apiFetch<{ status: string }>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}`, { method: 'DELETE', body }),
    closeBudget: (projectId: string, versionId: string, lineId: string, body: { finalSpend: number; reason?: string }) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/close-budget`, { method: 'POST', body }),
    reopenBudget: (projectId: string, versionId: string, lineId: string, body: { reason: string }) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/reopen-budget`, { method: 'POST', body }),
    setVwOverride: (projectId: string, versionId: string, lineId: string, body: { override: 'AUTO' | 'MANUAL_COMPLETE' | 'MANUAL_NOT_APPLICABLE'; reason?: string }) =>
      apiFetch<ApiBomLineItem>(`/api/v1/projects/${projectId}/bom/${versionId}/lines/${lineId}/vw-override`, { method: 'POST', body }),
    setVwOverrideBulk: (projectId: string, versionId: string, body: { lineIds: string[]; override: 'AUTO' | 'MANUAL_COMPLETE' | 'MANUAL_NOT_APPLICABLE'; reason?: string }) =>
      apiFetch<{ updated: number }>(`/api/v1/projects/${projectId}/bom/${versionId}/vw-override/bulk`, { method: 'POST', body }),
    baselineReconcileDiff: (projectId: string) =>
      apiFetch<{ diff: ApiJbReconcileDiffEntry[] }>(`/api/v1/projects/${projectId}/baseline-reconcile`, { method: 'POST', body: { apply: false } }),
    baselineReconcileApply: (projectId: string, lineIds: string[]) =>
      apiFetch<{ corrected: string[]; skipped: { lineId: string; reason: string }[] }>(`/api/v1/projects/${projectId}/baseline-reconcile`, { method: 'POST', body: { apply: true, lineIds } }),
    snapshots: (projectId: string, q: { limit?: number; offset?: number } = {}) =>
      apiFetch<ApiSnapshotListResponse>(`/api/v1/projects/${projectId}/snapshots`, {
        query: { limit: q.limit?.toString(), offset: q.offset?.toString() },
      }),
    snapshot: (projectId: string, snapshotId: string) =>
      apiFetch<ApiSnapshotDetail>(`/api/v1/projects/${projectId}/snapshots/${snapshotId}`),
    createSnapshot: (projectId: string, body: { label: string; description?: string | null }) =>
      apiFetch<ApiSnapshotDetail>(`/api/v1/projects/${projectId}/snapshots`, { method: 'POST', body }),
    snapshotDiff: (projectId: string, snapshotId: string, compareTo: string) =>
      apiFetch<ApiSnapshotDiffResponse>(`/api/v1/projects/${projectId}/snapshots/${snapshotId}/diff`, {
        query: { compareTo },
      }),
    bomLineHistory: (projectId: string, lineId: string) =>
      apiFetch<ApiBomLineHistoryResponse>(`/api/v1/projects/${projectId}/bom-lines/${lineId}/history`),
    revertBomLinesToSnapshot: (projectId: string, body: { snapshotId: string; lineIds: string[] }) =>
      apiFetch<ApiRevertResponse>(`/api/v1/projects/${projectId}/bom-lines/revert`, { method: 'POST', body }),

    // ─── M3 G2: on-site access (routes/onsite-staff.ts) ──────────────────────
    onsiteOverview: (projectId: string) =>
      apiFetch<ApiOnsiteOverview>(`/api/v1/projects/${projectId}/onsite`),
    createOnsiteCode: (projectId: string) =>
      apiFetch<ApiOnsiteCode>(`/api/v1/projects/${projectId}/onsite/code`, { method: 'POST' }),
    revokeOnsiteCode: (projectId: string) =>
      apiFetch<{ success: true }>(`/api/v1/projects/${projectId}/onsite/code`, { method: 'DELETE' }),
    revokeOnsiteSession: (projectId: string, sessionId: string) =>
      apiFetch<{ success: true }>(`/api/v1/projects/${projectId}/onsite/sessions/${sessionId}`, { method: 'DELETE' }),
    onsiteIssues: (projectId: string) =>
      apiFetch<ApiOnsiteIssue[]>(`/api/v1/projects/${projectId}/onsite/issues`),
    resolveOnsiteIssue: (projectId: string, issueId: string, body: { resolved: boolean; resolutionNote?: string }) =>
      apiFetch<ApiOnsiteIssue>(`/api/v1/projects/${projectId}/onsite/issues/${issueId}`, { method: 'PATCH', body }),
  },

  procurement: {
    pos: (q: Record<string, string> = {}) =>
      apiFetch<ApiPO[]>('/api/v1/procurement/pos', { query: q }),
    vendors: () => apiFetch<ApiVendor[]>('/api/v1/procurement/vendors'),
    shipToAddresses: () =>
      apiFetch<{ addresses: ApiShipToAddress[] }>('/api/v1/procurement/ship-to-addresses'),
    createPo: (body: ApiCreatePoBody) =>
      apiFetch<ApiPO>('/api/v1/procurement/pos', { method: 'POST', body }),
    // Preview of the PO number a create with this project + series would get. No lock —
    // allocatePoNumber (called at actual create time) is the source of truth.
    nextPoNumber: (projectId: string, series: 100 | 200 | 300 | 400 | 500) =>
      apiFetch<{ poNumber: string; series: number; label: string }>('/api/v1/procurement/pos/next-number', {
        query: { projectId, series: String(series) },
      }),
    updatePo: (id: string, body: ApiUpdatePoBody) =>
      apiFetch<ApiPO>(`/api/v1/procurement/pos/${id}`, { method: 'PATCH', body }),
    getPo: (id: string) => apiFetch<ApiPoDetail>(`/api/v1/procurement/pos/${id}`),
    deletePo: (id: string) => apiFetch<void>(`/api/v1/procurement/pos/${id}`, { method: 'DELETE' }),
    recordPoPayment: (poId: string, body: ApiRecordPoPaymentBody) =>
      apiFetch<ApiPaymentRecord>(`/api/v1/procurement/pos/${poId}/payments`, { method: 'POST', body }),
    deletePoPayment: (poId: string, paymentId: string) =>
      apiFetch<{ deleted: boolean }>(`/api/v1/procurement/pos/${poId}/payments/${paymentId}`, { method: 'DELETE' }),
    resyncProjectPaidStatus: (projectId: string) =>
      apiFetch<{ resynced: number; pos: Array<{ id: string; status: string }> }>(`/api/v1/procurement/projects/${projectId}/resync-paid`, { method: 'POST' }),
    createVendor: (body: ApiCreateVendorBody) =>
      apiFetch<ApiVendor>('/api/v1/procurement/vendors', { method: 'POST', body }),
    updateVendor: (id: string, body: ApiUpdateVendorBody) =>
      apiFetch<ApiVendor>(`/api/v1/procurement/vendors/${id}`, { method: 'PATCH', body }),
    vendors1099: () =>
      apiFetch<ApiVendor1099Report>('/api/v1/procurement/vendors/1099'),
  },

  invoicing: {
    list: (q: Record<string, string> = {}) =>
      apiFetch<ApiInvoice[]>('/api/v1/invoicing/invoices', { query: q }),
    cashFlow: () => apiFetch<ApiCashFlow>('/api/v1/invoicing/cash-flow'),
    cashFlowDetail: () => apiFetch<ApiCashFlowDetail>('/api/v1/invoicing/cash-flow/detail'),
    createInvoice: (body: ApiCreateInvoiceBody) =>
      apiFetch<ApiInvoice>('/api/v1/invoicing/invoices', { method: 'POST', body }),
    issueInvoice: (id: string) =>
      apiFetch<ApiInvoice>(`/api/v1/invoicing/invoices/${id}/issue`, { method: 'POST' }),
    updateInvoice: (id: string, body: ApiUpdateInvoiceBody) =>
      apiFetch<ApiInvoice>(`/api/v1/invoicing/invoices/${id}`, { method: 'PATCH', body }),
    // A4: hard delete for an invoice entered in error. Voiding keeps a real invoice on
    // the books; this removes one that should never have existed (audit row retains it).
    deleteInvoice: (id: string) =>
      apiFetch<{ ok: true }>(`/api/v1/invoicing/invoices/${id}`, { method: 'DELETE' }),
    recordPayment: (body: ApiRecordPaymentBody) =>
      apiFetch<{ id: string }>('/api/v1/invoicing/payments', { method: 'POST', body }),
    voidPayment: (id: string, reason?: string) =>
      apiFetch<{ status: 'voided' }>(`/api/v1/invoicing/payments/${id}/void`, {
        method: 'POST',
        body: { reason },
      }),
    fixedCosts: () => apiFetch<ApiFixedCost[]>('/api/v1/invoicing/fixed-costs'),
    createFixedCost: (body: { label: string; amount: number; sortOrder?: number }) =>
      apiFetch<ApiFixedCost>('/api/v1/invoicing/fixed-costs', { method: 'POST', body }),
    updateFixedCost: (id: string, body: { label?: string; amount?: number; active?: boolean; sortOrder?: number }) =>
      apiFetch<ApiFixedCost>(`/api/v1/invoicing/fixed-costs/${id}`, { method: 'PATCH', body }),
    deleteFixedCost: (id: string) =>
      apiFetch<void>(`/api/v1/invoicing/fixed-costs/${id}`, { method: 'DELETE' }),
    financialSnapshots: () =>
      apiFetch<Record<string, { value: number; updatedAt: string }>>('/api/v1/invoicing/financial-snapshots'),
    updateSnapshot: (key: string, value: number) =>
      apiFetch<{ key: string; value: number; updatedAt: string }>(`/api/v1/invoicing/financial-snapshots/${key}`, { method: 'PATCH', body: { value } }),
  },

  reporting: {
    portfolio: () => apiFetch<ApiPortfolioSummary>('/api/v1/reporting/portfolio'),
    dashboard: () => apiFetch<ApiDashboardData>('/api/v1/reporting/dashboard'),
    byStream: () => apiFetch<ApiStreamGroup[]>('/api/v1/reporting/by-stream'),
    byPm: () => apiFetch<ApiPmGroup[]>('/api/v1/reporting/by-pm'),
    margin: () => apiFetch<ApiMarginRow[]>('/api/v1/reporting/margin'),
    apAging: () => apiFetch<ApiApAging>('/api/v1/reporting/ap-aging'),
    aiUsage: (params: { from?: string; to?: string; groupBy?: string } = {}) =>
      apiFetch<ApiAiUsageReport>('/api/v1/reporting/ai-usage', { query: params as Record<string, string> }),
  },

  settings: {
    thresholds: () => apiFetch<ApiApprovalThreshold[]>('/api/v1/settings/approval-thresholds'),
    createThreshold: (body: ApiCreateThresholdBody) =>
      apiFetch<ApiApprovalThreshold>('/api/v1/settings/approval-thresholds', { method: 'POST', body }),
    updateThreshold: (id: string, body: Partial<ApiCreateThresholdBody> & { active?: boolean }) =>
      apiFetch<ApiApprovalThreshold>(`/api/v1/settings/approval-thresholds/${id}`, { method: 'PATCH', body }),
    deleteThreshold: (id: string) =>
      apiFetch<void>(`/api/v1/settings/approval-thresholds/${id}`, { method: 'DELETE' }),
    pendingApprovals: () => apiFetch<ApiPendingApproval[]>('/api/v1/settings/approvals/pending'),
    decideApproval: (id: string, body: { decision: 'APPROVED' | 'REJECTED'; notes?: string }) =>
      apiFetch<any>(`/api/v1/settings/approvals/${id}/decide`, { method: 'POST', body }),
    updateUser: (id: string, body: { role?: string; location?: string | null; active?: boolean }) =>
      apiFetch<any>(`/api/v1/ref/users/${id}`, { method: 'PATCH', body }),
  },

  jetbuilt: {
    searchProjects: (query?: string, page?: number) =>
      apiFetch<ApiJetbuiltProject[]>('/api/v1/jetbuilt/projects', { query: { ...(query ? { query } : {}), ...(page ? { page: String(page) } : {}) } }),
    getProject: (id: number) =>
      apiFetch<ApiJetbuiltProjectDetail>(`/api/v1/jetbuilt/projects/${id}`),
    getOptions: (projectId: number) =>
      apiFetch<ApiJetbuiltOption[]>(`/api/v1/jetbuilt/projects/${projectId}/options`),
  },

  toggl: {
    projectHours: (projectCode: string) =>
      apiFetch<ApiTogglProjectHours>(`/api/v1/toggl/project-hours/${projectCode}`),
  },

  warehouse: {
    // PO receiving
    outstandingPos: (q: Record<string, string> = {}) =>
      apiFetch<ApiReceivingPO[]>('/api/v1/warehouse/pos/outstanding', { query: q }),
    receivedPos: (q: Record<string, string> = {}) =>
      apiFetch<ApiReceivingPO[]>('/api/v1/warehouse/pos/received', { query: q }),
    getPo: (id: string) => apiFetch<ApiReceivingPoDetail>(`/api/v1/warehouse/pos/${id}`),
    getPoByTracking: (trackingNumber: string) =>
      apiFetch<ApiVendorShipmentWithPo>(`/api/v1/warehouse/pos/by-tracking/${encodeURIComponent(trackingNumber)}`),
    receivePo: (id: string, body: ApiReceivePoItemsBody) =>
      apiFetch<ApiReceiveResult>(`/api/v1/warehouse/pos/${id}/receive`, { method: 'POST', body }),
    // R8 — every tracked item ever received against this PO (every receiving pass, not just
    // the latest), for Warehouse Mode Check-in's "Print all labels for this PO" button.
    poItemsForLabels: (id: string) =>
      apiFetch<ApiPoItemsForLabels>(`/api/v1/warehouse/pos/${id}/items`),

    // OFE — Owner Furnished Equipment, no PO involved
    ofeBomLines: (projectId: string) =>
      apiFetch<ApiOfeBomLine[]>('/api/v1/warehouse/ofe/bom-lines', { query: { projectId } }),
    receiveOfe: (body: ApiReceiveOfeBody) =>
      apiFetch<ApiReceiveOfeResult>('/api/v1/warehouse/receive-ofe', { method: 'POST', body }),

    // Stock without projects (M1-2 API / M1-5 UI, Stock tab M1-7) — quantity-by-model shelf
    // inventory, never a Project row (warehouse-session-2026-09-22.md Round 2).
    stockSummary: (q: Record<string, string> = {}) =>
      apiFetch<ApiStockSummary>('/api/v1/warehouse/stock', { query: q }),
    // R15 (docs/specs/warehouse-round3-feedback.md): the manufacturer/shelfId/shelfIds/
    // hasBarcode/entryType filters live alongside warehouse/key in the same `q` bag `q` takes
    // above — `opts` here just carries the ones a drill-in drawer cares about (the shelf scope
    // a hierarchy node narrowed to), so a filtered/hierarchy view stays scoped once you open a
    // group's individual items.
    stockItems: (key: string, opts: { warehouse?: 'OKC' | 'ATL'; shelfId?: string; shelfIds?: string } = {}) =>
      apiFetch<ApiStockItem[]>('/api/v1/warehouse/stock/items', {
        query: {
          key,
          ...(opts.warehouse ? { warehouse: opts.warehouse } : {}),
          ...(opts.shelfIds ? { shelfIds: opts.shelfIds } : opts.shelfId ? { shelfId: opts.shelfId } : {}),
        },
      }),
    stockModelSuggestions: (q: string) =>
      apiFetch<{ suggestions: ApiStockModelSuggestion[] }>('/api/v1/warehouse/stock/model-suggestions', { query: { q } }),
    // R15: the distinct manufacturers currently on a shelf, for the filter's searchable list.
    stockManufacturers: () =>
      apiFetch<{ manufacturers: string[] }>('/api/v1/warehouse/stock/manufacturers'),
    // R15: warehouse -> shelf path (Sortly folders) hierarchy, for the Stock tab's tree/
    // breadcrumb drill-down. Takes the same warehouse/search/manufacturer/hasBarcode/entryType
    // filters as stockSummary; a client narrows the flat table to one tree node by re-calling
    // stockSummary with that node's own `shelfIds`.
    stockTree: (q: Record<string, string> = {}) =>
      apiFetch<{ tree: ApiStockTreeNode[] }>('/api/v1/warehouse/stock/tree', { query: q }),
    addToStock: (body: ApiAddToStockBody) =>
      apiFetch<ApiAddToStockResult | ApiAddQuantityEntryResult>('/api/v1/warehouse/stock/items', { method: 'POST', body }),

    // R13 — "Stock Check Sheet": a project's outstanding BOM lines that already match
    // something on a warehouse shelf, plus exactly which shelf(s) hold it, for the printable
    // pull list procurement hands the warehouse before cutting a stock PO.
    stockCheck: (projectId: string) =>
      apiFetch<ApiStockCheckSheet>(`/api/v1/warehouse/projects/${projectId}/stock-check`),

    // F6 — Count Sheet / Variance Report data (Stock tab "Count sheet" button). warehouse is
    // effectively required by the UI (one warehouse per printed sheet); shelfIds/batchId scope
    // it further, filter is only meaningful once counting has started ('variance' | 'uncounted').
    stockCountReport: (q: { warehouse?: 'OKC' | 'ATL'; shelfIds?: string; batchId?: string; filter?: 'variance' | 'uncounted' } = {}) =>
      apiFetch<ApiStockCountReport>('/api/v1/warehouse/stock-count/report', {
        query: {
          ...(q.warehouse ? { warehouse: q.warehouse } : {}),
          ...(q.shelfIds ? { shelfIds: q.shelfIds } : {}),
          ...(q.batchId ? { batchId: q.batchId } : {}),
          ...(q.filter ? { filter: q.filter } : {}),
        },
      }),

    // F6 — Warehouse Mode's Count mode (CountMode.vue): walk one shelf, GET its expected
    // list, count everything, POST the whole shelf at once ("Shelf done" — nothing saves
    // before that). `resolve` backs the "Found here" card for a scan that isn't on the
    // current shelf's expected list; `progress` backs the shelves-counted/remaining view.
    countShelf: (shelfId: string) =>
      apiFetch<ApiCountShelfExpected>(`/api/v1/warehouse/stock-count/shelves/${shelfId}`),
    countResolve: (code: string) =>
      apiFetch<ApiCountResolveResult>(`/api/v1/warehouse/stock-count/resolve/${encodeURIComponent(code)}`),
    saveCount: (shelfId: string, body: ApiCountSaveBody) =>
      apiFetch<ApiCountSaveResult>(`/api/v1/warehouse/stock-count/shelves/${shelfId}`, { method: 'POST', body }),
    countProgress: (warehouse: 'OKC' | 'ATL') =>
      apiFetch<ApiCountProgress>('/api/v1/warehouse/stock-count/progress', { query: { warehouse } }),

    // Stock CRUD (M5 W4, docs/specs/warehouse-round2-feedback.md "Stock has no CRUD") — edit,
    // soft delete (reason required), move (single item or bulk) for job-less stock items.
    updateStockItem: (id: string, body: ApiUpdateStockItemBody) =>
      apiFetch<ApiTrackedItem>(`/api/v1/warehouse/stock/items/${id}`, { method: 'PATCH', body }),
    deleteStockItem: (id: string, reason: string) =>
      apiFetch<{ ok: true; item: ApiTrackedItem }>(`/api/v1/warehouse/stock/items/${id}`, { method: 'DELETE', body: { reason } }),
    moveStockItems: (body: ApiMoveStockItemsBody) =>
      apiFetch<ApiMoveStockItemsResult>('/api/v1/warehouse/stock/items/move', { method: 'POST', body }),

    // Sortly import (M5 #25/G3, docs/specs/warehouse-m5-stock-sortly-assets.md) — one-time
    // migration off Sortly. Same multipart pattern as pdfIntel uploads above: FormData body,
    // empty headers so ofetch/the browser sets its own multipart boundary. Dry run and commit
    // hit the exact same route (routes/warehouse/stock.ts POST /stock/import/sortly) — the
    // caller re-uploads the same File for commit so the server re-parses and re-checks
    // idempotency at write time rather than trusting a stale preview.
    sortlyImportPreview: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<ApiSortlyPreviewResult>('/api/v1/warehouse/stock/import/sortly', {
        method: 'POST',
        query: { dryRun: '1' },
        body: form as any,
        headers: {},
      })
    },
    sortlyImportCommit: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<ApiSortlyCommitResult>('/api/v1/warehouse/stock/import/sortly', {
        method: 'POST',
        body: form as any,
        headers: {},
      })
    },

    // Re-issue by label / Pull (M1-3 API, M1-6 UI) — fulfilling an IN_STOCK_OKC/ATL BOM line
    // by scanning the item's existing label, reassigning that ID to the job.
    pullTargets: (search?: string) =>
      apiFetch<ApiPullTarget[]>('/api/v1/warehouse/stock/pull-targets', { query: search ? { search } : {} }),
    pullContext: (projectId: string) =>
      apiFetch<ApiPullContext>('/api/v1/warehouse/stock/pull-context', { query: { projectId } }),
    // F3: the CreatePoModal (series 500) line picker's source.
    stockPoEligibleLines: (projectId: string) =>
      apiFetch<{ lines: ApiStockPoEligibleLine[] }>('/api/v1/warehouse/stock/stock-po-eligible-lines', { query: { projectId } }),
    // F3: Warehouse Mode Pull's rail, once an open 500-series PO is picked/scanned.
    pullPo: (id: string) =>
      apiFetch<ApiPullPo>(`/api/v1/warehouse/stock/pull-po/${id}`),
    reissue: (body: ApiReissueBody) =>
      apiFetch<ApiReissueResult>('/api/v1/warehouse/stock/reissue', { method: 'POST', body }),
    // F5: pull against a quantity entry (scan a manufacturer barcode, or pick make/model).
    reissueEntry: (body: ApiReissueEntryBody) =>
      apiFetch<ApiReissueEntryResult>('/api/v1/warehouse/stock/reissue-entry', { method: 'POST', body }),
    reissueQuantity: (body: ApiReissueQuantityBody) =>
      apiFetch<ApiReissueQuantityResult>('/api/v1/warehouse/stock/reissue-quantity', { method: 'POST', body }),
    reissueBulk: (body: ApiReissueBulkBody) =>
      apiFetch<ApiReissueBulkResult>('/api/v1/warehouse/stock/reissue-bulk', { method: 'POST', body }),
    undoReissue: (assignmentId: string) =>
      apiFetch<ApiUndoReissueResult>(`/api/v1/warehouse/stock/reissue/${assignmentId}/undo`, { method: 'POST' }),
    releaseToStock: (body: ApiReleaseToStockBody) =>
      apiFetch<ApiTrackedItem>('/api/v1/warehouse/stock/release', { method: 'POST', body }),

    // Tracked items
    lookupItem: (code: string) => apiFetch<ApiTrackedItemDetail>(`/api/v1/warehouse/items/lookup/${encodeURIComponent(code)}`),
    listItems: (q: Record<string, string> = {}) =>
      apiFetch<ApiTrackedItem[]>('/api/v1/warehouse/items', { query: q }),
    updateItem: (id: string, body: ApiUpdateItemBody) =>
      apiFetch<ApiTrackedItem>(`/api/v1/warehouse/items/${id}`, { method: 'PATCH', body }),
    deployItem: (id: string, body?: { notes?: string }) =>
      apiFetch<ApiTrackedItem>(`/api/v1/warehouse/items/${id}/deploy`, { method: 'POST', body: body ?? {} }),
    createItem: (body: ApiCreateItemBody) =>
      apiFetch<ApiTrackedItem>('/api/v1/warehouse/items/create', { method: 'POST', body }),
    // Bulk serial capture (M4 G1) — one call from SerialCaptureStep, one transaction server-side.
    assignSerials: (assignments: ApiSerialAssignment[]) =>
      apiFetch<{ items: ApiTrackedItem[] }>('/api/v1/warehouse/items/serials', { method: 'PATCH', body: { assignments } }),

    // Containers
    listContainers: (q: Record<string, string> = {}) =>
      apiFetch<ApiContainer[]>('/api/v1/warehouse/containers', { query: q }),
    lookupContainer: (code: string) =>
      apiFetch<ApiContainerDetail>(`/api/v1/warehouse/containers/lookup/${encodeURIComponent(code)}`),
    createContainer: (body: ApiCreateContainerBody) =>
      apiFetch<ApiContainer>('/api/v1/warehouse/containers', { method: 'POST', body }),
    addItemsToContainer: (containerId: string, itemIds: string[]) =>
      apiFetch<{ success: boolean }>(`/api/v1/warehouse/containers/${containerId}/add-items`, { method: 'POST', body: { itemIds } }),
    /** Add by scanned/typed code (SKY- id, bare encodedId or serial) — resolved server-side. */
    addItemsToContainerByCode: (containerId: string, codes: string[]) =>
      apiFetch<{ success: boolean; itemCount: number; containerLocation: string }>(`/api/v1/warehouse/containers/${containerId}/add-items`, { method: 'POST', body: { codes } }),
    removeItemsFromContainer: (containerId: string, itemIds: string[]) =>
      apiFetch<{ success: boolean }>(`/api/v1/warehouse/containers/${containerId}/remove-items`, { method: 'POST', body: { itemIds } }),
    addContainersToContainer: (containerId: string, containerIds: string[]) =>
      apiFetch<{ success: boolean }>(`/api/v1/warehouse/containers/${containerId}/add-containers`, { method: 'POST', body: { containerIds } }),
    updateContainer: (id: string, body: ApiUpdateContainerBody) =>
      apiFetch<ApiContainer>(`/api/v1/warehouse/containers/${id}`, { method: 'PATCH', body }),

    // Outbound shipments
    listOutboundShipments: (q: Record<string, string> = {}) =>
      apiFetch<ApiOutboundShipment[]>('/api/v1/warehouse/outbound-shipments', { query: q }),
    getOutboundShipment: (id: string) =>
      apiFetch<ApiOutboundShipment>(`/api/v1/warehouse/outbound-shipments/${id}`),
    createOutboundShipment: (body: ApiCreateOutboundShipmentBody) =>
      apiFetch<ApiOutboundShipment>('/api/v1/warehouse/outbound-shipments', { method: 'POST', body }),
    updateOutboundShipment: (id: string, body: {
      carrierTracking?: string | null; notes?: string | null
      // R12: destination address / expected arrival, editable same as tracking+notes.
      destinationAddress?: string | null; expectedArrival?: string | null
    }) =>
      apiFetch<ApiOutboundShipment>(`/api/v1/warehouse/outbound-shipments/${id}`, { method: 'PATCH', body }),
    addToShipment: (id: string, body: { itemIds?: string[]; containerIds?: string[] }) =>
      apiFetch<ApiOutboundShipment>(`/api/v1/warehouse/outbound-shipments/${id}/add`, { method: 'POST', body }),
    removeFromShipment: (id: string, body: { itemIds?: string[]; containerIds?: string[] }) =>
      apiFetch<ApiOutboundShipment>(`/api/v1/warehouse/outbound-shipments/${id}/remove`, { method: 'POST', body }),
    confirmShipment: (id: string, body?: { carrierTracking?: string; notes?: string }) =>
      apiFetch<{ success: boolean }>(`/api/v1/warehouse/outbound-shipments/${id}/confirm`, { method: 'POST', body: body ?? {} }),
    arriveShipment: (id: string, body?: { notes?: string }) =>
      apiFetch<{ success: boolean }>(`/api/v1/warehouse/outbound-shipments/${id}/arrive`, { method: 'POST', body: body ?? {} }),
    // R12: prefill helper — the destination project's account address, or null.
    getProjectDestinationAddress: (projectId: string) =>
      apiFetch<{ address: string | null }>(`/api/v1/warehouse/outbound-shipments/project-address/${projectId}`),
    // R13 (no written spec — warehouse team bug report): Warehouse Mode's Move-to-jobsite/
    // other-warehouse bridge. Creates a real IN_TRANSIT shipment on the first scan of a
    // destination session (shipmentId omitted), or continues one (shipmentId given — every
    // other field beyond itemIds/containerIds is ignored server-side in that case).
    moveToShipment: (body: {
      shipmentId?: string
      itemIds?: string[]; containerIds?: string[]
      destination: LocationState; transitType?: TransitType
      destinationAddress?: string; expectedArrival?: string; projectId?: string; notes?: string
    }) =>
      apiFetch<ApiOutboundShipment>('/api/v1/warehouse/outbound-shipments/move', { method: 'POST', body }),
    // R13: Warehouse Mode Move's Undo, for an item/container that was put into transit on the
    // given shipment — moves it back to toLocation and detaches it (deleting the shipment if
    // that was the last thing on it).
    undoOutboundShipmentMove: (shipmentId: string, body: {
      itemId?: string; containerId?: string; toLocation: string; notes?: string
    }) =>
      apiFetch<{ success: boolean }>(`/api/v1/warehouse/outbound-shipments/${shipmentId}/undo-item`, { method: 'POST', body }),

    // Movement
    ship: (body: ApiShipBody) =>
      apiFetch<{ shipmentId: string }>('/api/v1/warehouse/ship', { method: 'POST', body }),
    arrive: (body: ApiArriveBody) =>
      apiFetch<{ success: boolean }>('/api/v1/warehouse/arrive', { method: 'POST', body }),
    movements: (q: Record<string, string> = {}) =>
      apiFetch<ApiMovementEvent[]>('/api/v1/warehouse/movements', { query: q }),

    // Vendor shipments
    listVendorShipments: (q: Record<string, string> = {}) =>
      apiFetch<ApiVendorShipment[]>('/api/v1/warehouse/vendor-shipments', { query: q }),
    getVendorShipment: (id: string) =>
      apiFetch<ApiVendorShipmentDetail>(`/api/v1/warehouse/vendor-shipments/${id}`),
    createVendorShipment: (body: ApiCreateVendorShipmentBody) =>
      apiFetch<ApiVendorShipmentDetail>('/api/v1/warehouse/vendor-shipments', { method: 'POST', body }),
    updateVendorShipment: (id: string, body: ApiUpdateVendorShipmentBody) =>
      apiFetch<ApiVendorShipmentDetail>(`/api/v1/warehouse/vendor-shipments/${id}`, { method: 'PATCH', body }),
    // F1/J2: cross-project aggregate of expected arrivals for the warehouse
    // dashboard, grouped by arrival date. window: 'today' | 'week' (default),
    // or pass explicit from/to (YYYY-MM-DD) for a custom range.
    vendorShipmentArrivals: (q: { window?: 'today' | 'week'; from?: string; to?: string } = {}) =>
      apiFetch<ApiVendorShipmentArrivals>('/api/v1/warehouse/vendor-shipments/arrivals', { query: q as Record<string, string> }),
    receiveFromPackingList: (shipmentId: string) =>
      apiFetch<{ action: 'EXTRACTED' | 'ALREADY_EXTRACTED'; extractionId: string }>(
        `/api/v1/warehouse/vendor-shipments/${shipmentId}/receive-from-packing-list`,
        { method: 'POST' },
      ),

    // Discrepancies
    listDiscrepancies: (q: Record<string, string> = {}) =>
      apiFetch<ApiDiscrepancy[]>('/api/v1/warehouse/discrepancies', { query: q }),
    createDiscrepancy: (body: ApiCreateDiscrepancyBody) =>
      apiFetch<ApiDiscrepancy>('/api/v1/warehouse/discrepancies', { method: 'POST', body }),
    updateDiscrepancy: (id: string, body: ApiUpdateDiscrepancyBody) =>
      apiFetch<ApiDiscrepancy>(`/api/v1/warehouse/discrepancies/${id}`, { method: 'PATCH', body }),

    // RMA (M4 G2 server / G3 UI, docs/specs/warehouse-m4-serials-rma-packing.md "RMA") —
    // vendor return tracking for a TrackedItem. PATCH /rma/:id is one discriminated-union
    // endpoint on the server (action: ISSUE/SHIP/RESOLVE/CANCEL); these four helpers just
    // shape the body per action so callers don't have to know that.
    listRma: (q: Record<string, string> = {}) =>
      apiFetch<ApiRmaItem[]>('/api/v1/warehouse/rma', { query: q }),
    getRma: (id: string) =>
      apiFetch<ApiRmaItem>(`/api/v1/warehouse/rma/${id}`),
    createRma: (body: { trackedItemId: string; reason: string; vendor?: string }) =>
      apiFetch<ApiRmaItem>('/api/v1/warehouse/rma', { method: 'POST', body }),
    issueRma: (id: string, rmaNumber: string) =>
      apiFetch<ApiRmaItem>(`/api/v1/warehouse/rma/${id}`, { method: 'PATCH', body: { action: 'ISSUE', rmaNumber } }),
    shipRma: (id: string, trackingNumber?: string) =>
      apiFetch<ApiRmaItem>(`/api/v1/warehouse/rma/${id}`, { method: 'PATCH', body: { action: 'SHIP', ...(trackingNumber ? { trackingNumber } : {}) } }),
    resolveRma: (id: string, body: {
      resolutionType: ApiRmaResolutionType
      replacementItemId?: string
      replacementCode?: string
      creditAmount?: number
      notes?: string
    }) =>
      apiFetch<ApiRmaItem>(`/api/v1/warehouse/rma/${id}`, { method: 'PATCH', body: { action: 'RESOLVE', ...body } }),
    cancelRma: (id: string, notes?: string) =>
      apiFetch<ApiRmaItem>(`/api/v1/warehouse/rma/${id}`, { method: 'PATCH', body: { action: 'CANCEL', ...(notes ? { notes } : {}) } }),

    // Project materials
    projectMaterials: (projectId: string) =>
      apiFetch<ApiProjectMaterials>(`/api/v1/warehouse/project/${projectId}/materials`),

    // Labels
    generateItemLabel: (itemId: string) =>
      apiFetch<{ zpl: string; skylarkId: string }>('/api/v1/warehouse/labels/item', { method: 'POST', body: { itemId } }),
    generateContainerLabel: (containerId: string) =>
      apiFetch<{ zpl: string; containerId: string }>('/api/v1/warehouse/labels/container', { method: 'POST', body: { containerId } }),
    generateItemLabelsBatch: (itemIds: string[]) =>
      apiFetch<{ labels: { skylarkId: string; zpl: string }[] }>('/api/v1/warehouse/labels/items/batch', { method: 'POST', body: { itemIds } }),
    // L4 (docs/specs/label-printing-architecture.md, warehouse-outstanding.md P5): the
    // permanent 2-across device label. `policy` is only sent by the at-receive print path
    // (useLabelPrinter.printDeviceLabelsAtReceive) — reprint-on-demand omits it and always
    // prints every id it's given.
    generateDeviceLabel: (itemIds: string[], opts: { dpi?: 203 | 300 | 600; policy?: 'OFF' | 'NON_CONSUMABLES' | 'ALL' } = {}) =>
      apiFetch<{ zpl: string; count: number; skippedCount: number; itemIds: string[] }>(
        '/api/v1/warehouse/labels/device', { method: 'POST', body: { itemIds, ...opts } },
      ),
    // "Device labels at receive" — per-warehouse, decoupled from whether a DEVICE printer row
    // exists yet (see WarehouseDeviceLabelPolicy in schema.prisma).
    getDeviceLabelPolicy: (warehouse: string) =>
      apiFetch<{ warehouse: string; policy: 'OFF' | 'NON_CONSUMABLES' | 'ALL' }>(
        '/api/v1/warehouse/device-label-policy', { query: { warehouse } },
      ),
    setDeviceLabelPolicy: (warehouse: string, policy: 'OFF' | 'NON_CONSUMABLES' | 'ALL') =>
      apiFetch<{ warehouse: string; policy: 'OFF' | 'NON_CONSUMABLES' | 'ALL' }>(
        '/api/v1/warehouse/device-label-policy', { method: 'PATCH', body: { warehouse, policy } },
      ),

    // M3 #20 — deferred site labels. GET previews the backlog (badge count); POST prints
    // and records the print (metadata.sitePackPrintedAt) so a reprint is explicit.
    siteLabelPackPreview: (projectId: string) =>
      apiFetch<ApiSiteLabelPackPreview>(`/api/v1/warehouse/project/${projectId}/site-label-pack`),
    printSiteLabelPack: (projectId: string, reprint = false, dpi?: 203 | 300 | 600) =>
      apiFetch<ApiSiteLabelPackResult>(`/api/v1/warehouse/project/${projectId}/site-label-pack`, {
        method: 'POST',
        body: { reprint, ...(dpi ? { dpi } : {}) },
      }),

    // Universal scan
    scan: (code: string) =>
      apiFetch<ApiScanResult>(`/api/v1/warehouse/scan/${encodeURIComponent(code)}`),

    // Bulk move
    moveItems: (body: { itemIds: string[]; toLocation?: string; toContainerId?: string; notes?: string }) =>
      apiFetch<{ success: boolean; movedCount: number }>('/api/v1/warehouse/items/move', { method: 'POST', body }),
    moveContainers: (body: { containerIds: string[]; toLocation: string; notes?: string }) =>
      apiFetch<{ success: boolean; movedCount: number }>('/api/v1/warehouse/containers/move', { method: 'POST', body }),

    // Put-away
    pendingPutAway: (q: Record<string, string> = {}) =>
      apiFetch<any[]>('/api/v1/warehouse/put-away/pending', { query: q }),
    putAway: (body: { itemIds: string[]; containerId?: string; newContainer?: any; notes?: string }) =>
      apiFetch<{ success: boolean; container: any; itemCount: number }>('/api/v1/warehouse/put-away', { method: 'POST', body }),

    // Returns
    listReturns: (q: Record<string, string> = {}) =>
      apiFetch<any[]>('/api/v1/warehouse/returns', { query: q }),
    getReturn: (id: string) =>
      apiFetch<any>(`/api/v1/warehouse/returns/${id}`),
    createReturn: (body: any) =>
      apiFetch<any>('/api/v1/warehouse/returns', { method: 'POST', body }),
    processReturn: (id: string, body?: any) =>
      apiFetch<any>(`/api/v1/warehouse/returns/${id}/process`, { method: 'POST', body: body ?? {} }),
    returnReconciliation: (q: Record<string, string> = {}) =>
      apiFetch<any>('/api/v1/warehouse/returns/reconciliation', { query: q }),

    // Return sessions (M2 G1 server / G3 UI, docs/specs/warehouse-m2-returns.md) — one trip
    // back from one job: OPEN while scanning, CLOSED once pushed back into stock. Nothing in
    // the UI calls the legacy /returns endpoints above any more.
    returnSessions: (q: Record<string, string> = {}) =>
      apiFetch<ApiReturnSession[]>('/api/v1/warehouse/returns/sessions', { query: q }),
    getReturnSession: (id: string) =>
      apiFetch<ApiReturnSessionDetail>(`/api/v1/warehouse/returns/sessions/${id}`),
    openReturnSession: (body: ApiOpenReturnSessionBody) =>
      apiFetch<ApiReturnSession>('/api/v1/warehouse/returns/sessions', { method: 'POST', body }),
    scanReturnSession: (id: string, code: string) =>
      apiFetch<ApiReturnScanResult>(`/api/v1/warehouse/returns/sessions/${id}/scan`, { method: 'POST', body: { code } }),
    addReturnLine: (id: string, body: ApiAddReturnLineBody) =>
      apiFetch<ApiReturnLine>(`/api/v1/warehouse/returns/sessions/${id}/lines`, { method: 'POST', body }),
    updateReturnLine: (id: string, lineId: string, body: ApiUpdateReturnLineBody) =>
      apiFetch<ApiReturnLine>(`/api/v1/warehouse/returns/sessions/${id}/lines/${lineId}`, { method: 'PATCH', body }),
    deleteReturnLine: (id: string, lineId: string) =>
      apiFetch<void>(`/api/v1/warehouse/returns/sessions/${id}/lines/${lineId}`, { method: 'DELETE' }),
    closeReturnSession: (id: string) =>
      apiFetch<ApiReturnSessionDetail>(`/api/v1/warehouse/returns/sessions/${id}/close`, { method: 'POST' }),
    // W11 review page — per-line CREDIT/KEEP recommendations (W7: never creates a credit
    // memo or touches bom_line_items; see routes/warehouse/returns.ts POST .../review).
    reviewReturnSession: (id: string, body: { decisions: ApiReviewDecisionBody[] }) =>
      apiFetch<ApiReturnSessionDetail>(`/api/v1/warehouse/returns/sessions/${id}/review`, { method: 'POST', body }),

    // Consumables
    listConsumables: (q: Record<string, string> = {}) =>
      apiFetch<any[]>('/api/v1/warehouse/consumables', { query: q }),
    createConsumable: (body: any) =>
      apiFetch<any>('/api/v1/warehouse/consumables', { method: 'POST', body }),
    updateConsumable: (id: string, body: any) =>
      apiFetch<any>(`/api/v1/warehouse/consumables/${id}`, { method: 'PATCH', body }),
    adjustConsumable: (id: string, body: { quantityChange: number; reason: string; projectId?: string; notes?: string }) =>
      apiFetch<any>(`/api/v1/warehouse/consumables/${id}/adjust`, { method: 'POST', body }),
    // Timothy #4: soft retire (reason required) — hidden from lists after this, row/history kept.
    deleteConsumable: (id: string, reason: string) =>
      apiFetch<{ ok: true; stock: any }>(`/api/v1/warehouse/consumables/${id}`, { method: 'DELETE', body: { reason } }),
    lowStock: () =>
      apiFetch<any[]>('/api/v1/warehouse/consumables/low-stock'),
    consumableHistory: (id: string) =>
      apiFetch<any>(`/api/v1/warehouse/consumables/${id}/history`),

    // Browse / Search
    browseLocations: () =>
      apiFetch<any[]>('/api/v1/warehouse/browse/locations'),
    browseLocation: (location: string) =>
      apiFetch<any>(`/api/v1/warehouse/browse/locations/${encodeURIComponent(location)}`),
    containerTree: (id: string) =>
      apiFetch<any>(`/api/v1/warehouse/browse/containers/${id}/tree`),
    search: (q: string) =>
      apiFetch<any>('/api/v1/warehouse/search', { query: { q } }),

    // Shelves
    listShelves: (q?: Record<string, string>) =>
      apiFetch<any[]>('/api/v1/warehouse/shelves', { query: q }),
    getShelf: (id: string) =>
      apiFetch<any>(`/api/v1/warehouse/shelves/${id}`),
    createShelf: (body: any) =>
      apiFetch<any>('/api/v1/warehouse/shelves', { method: 'POST', body }),
    generateShelves: (body: { location: string; sections: { prefix: string; count: number }[] }) =>
      apiFetch<any>('/api/v1/warehouse/shelves/generate', { method: 'POST', body }),
    updateShelf: (id: string, body: any) =>
      apiFetch<any>(`/api/v1/warehouse/shelves/${id}`, { method: 'PATCH', body }),
    assignToShelf: (id: string, body: { itemIds?: string[]; containerIds?: string[]; consumableIds?: string[] }) =>
      apiFetch<any>(`/api/v1/warehouse/shelves/${id}/assign`, { method: 'POST', body }),
    unassignFromShelf: (id: string, body: { itemIds?: string[]; containerIds?: string[]; consumableIds?: string[] }) =>
      apiFetch<any>(`/api/v1/warehouse/shelves/${id}/unassign`, { method: 'POST', body }),

    // Printers (L3 — printer registry with roles, docs/specs/label-printing-architecture.md)
    listPrinters: (q?: Record<string, string>) =>
      apiFetch<any[]>('/api/v1/warehouse/printers', { query: q }),
    resolvePrinter: (warehouse: string, role: string) =>
      apiFetch<any>('/api/v1/warehouse/printers/resolve', { query: { warehouse, role } }),
    createPrinter: (body: any) =>
      apiFetch<any>('/api/v1/warehouse/printers', { method: 'POST', body }),
    updatePrinter: (id: string, body: any) =>
      apiFetch<any>(`/api/v1/warehouse/printers/${id}`, { method: 'PATCH', body }),
    deactivatePrinter: (id: string) =>
      apiFetch<any>(`/api/v1/warehouse/printers/${id}/deactivate`, { method: 'POST' }),
  },

  pair: {
    createSession: () =>
      apiFetch<ApiPairSession>('/api/v1/pair/session', { method: 'POST' }),
    join: (code: string, deviceName?: string) =>
      apiFetch<ApiPairJoinResult>('/api/v1/pair/join', { method: 'POST', body: { code, deviceName } }),
    scan: (sessionId: string, deviceId: string, code: string) =>
      apiFetch<{ success: boolean; found: boolean; scanResult: any }>('/api/v1/pair/scan', { method: 'POST', body: { sessionId, deviceId, code } }),
    setMode: (sessionId: string, deviceId: string, mode: string) =>
      apiFetch<{ success: boolean }>('/api/v1/pair/mode', { method: 'POST', body: { sessionId, deviceId, mode } }),
    selectPo: (sessionId: string, deviceId: string, poId: string) =>
      apiFetch<{ success: boolean }>('/api/v1/pair/select-po', { method: 'POST', body: { sessionId, deviceId, poId } }),
    setCheckinPo: (sessionId: string, poId: string | null) =>
      apiFetch<{ success: boolean }>('/api/v1/pair/set-checkin-po', { method: 'POST', body: { sessionId, poId } }),
    getState: (sessionId: string, deviceId: string) =>
      apiFetch<ApiPairState>(`/api/v1/pair/state/${sessionId}/${deviceId}`),
    status: (sessionId: string) =>
      apiFetch<ApiPairSessionStatus>(`/api/v1/pair/session/${sessionId}`),
    end: (sessionId: string) =>
      apiFetch<{ success: boolean }>(`/api/v1/pair/session/${sessionId}`, { method: 'DELETE' }),

    // ── G4/G5: packing-list capture-mode relay ──────────────────────────────
    // Desktop puts the paired phone into photo-capture mode; the phone picks
    // this up via its existing getState() poll (currentMode becomes
    // 'CAPTURE_PACKING_LIST'). See routes/pair.ts's contract comment.
    captureStart: (sessionId: string, projectId: string, poId?: string) =>
      apiFetch<{ success: boolean; mode: string }>('/api/v1/pair/capture/start', {
        method: 'POST',
        body: { sessionId, projectId, poId },
      }),
    captureStop: (sessionId: string) =>
      apiFetch<{ success: boolean; mode: string }>('/api/v1/pair/capture/stop', { method: 'POST', body: { sessionId } }),
    // Phone-side: uploads 1-8 captured pages against the desktop's pair
    // session. Kicks off the streaming PACKING_LIST extraction server-side
    // and the desktop learns the extractionId over its own SSE stream
    // ('packing_list_capture') — this call's response is otherwise unused by
    // the phone beyond confirming the upload landed.
    uploadCapturePhotos: (sessionId: string, deviceId: string, files: File[]) => {
      const form = new FormData()
      for (const f of files) form.append('files', f)
      const qs = new URLSearchParams({ sessionId, deviceId })
      return apiFetch<{ success: boolean; extractionId: string }>(`/api/v1/pair/capture/photos?${qs}`, {
        method: 'POST',
        body: form as any,
        headers: {},
      })
    },
  },

  ref: {
    users: () => apiFetch<ApiRefUser[]>('/api/v1/ref/users'),
    accounts: () => apiFetch<ApiRefAccount[]>('/api/v1/ref/accounts'),
    createAccount: (name: string) => apiFetch<ApiRefAccount>('/api/v1/ref/accounts', { method: 'POST', body: { name } }),
    opportunities: (q: { accountId?: string } = {}) =>
      apiFetch<{ id: string; code: string | null; name: string; accountId: string; revenueStream: string; status: string }[]>(
        '/api/v1/ref/opportunities', { query: q as any }
      ),
    trips: () => apiFetch<ApiTrip[]>('/api/v1/ref/trips'),
  },

  documents: {
    poData: (id: string) =>
      apiFetch<ApiDocumentPO>(`/api/v1/documents/po/${id}`),
    invoiceData: (id: string) =>
      apiFetch<ApiDocumentInvoice>(`/api/v1/documents/invoice/${id}`),
    containerPackingList: (containerId: string) =>
      apiFetch<ApiDocumentContainer>(`/api/v1/documents/packing-list/container/${containerId}`),
    projectPackingList: (projectId: string) =>
      apiFetch<ApiDocumentContainer[]>(`/api/v1/documents/packing-list/project/${projectId}`),
    creditMemoData: (id: string) =>
      apiFetch<ApiDocumentCreditMemo>(`/api/v1/documents/credit-memo/${id}`),
    changeOrderData: (id: string) =>
      apiFetch<ApiDocumentChangeOrder>(`/api/v1/documents/change-order/${id}`),
    logAudit: (body: { documentType: string; documentId: string; documentRef: string }) =>
      apiFetch<{ id: string }>('/api/v1/documents/audit', { method: 'POST', body }),
  },

  credits: {
    list: (projectId: string) =>
      apiFetch<ApiCreditMemo[]>(`/api/v1/credits/project/${projectId}`),
    create: (projectId: string, body: ApiCreateCreditMemoBody) =>
      apiFetch<ApiCreditMemo>(`/api/v1/credits/project/${projectId}`, { method: 'POST', body }),
    get: (id: string) =>
      apiFetch<ApiCreditMemo>(`/api/v1/credits/${id}`),
    update: (id: string, body: ApiUpdateCreditMemoBody) =>
      apiFetch<ApiCreditMemo>(`/api/v1/credits/${id}`, { method: 'PATCH', body }),
    delete: (id: string) =>
      apiFetch<void>(`/api/v1/credits/${id}`, { method: 'DELETE' }),
    addLine: (creditId: string, body: ApiCreateCreditMemoLineBody) =>
      apiFetch<ApiCreditMemoLine>(`/api/v1/credits/${creditId}/lines`, { method: 'POST', body }),
    updateLine: (creditId: string, lineId: string, body: Partial<ApiCreateCreditMemoLineBody>) =>
      apiFetch<ApiCreditMemoLine>(`/api/v1/credits/${creditId}/lines/${lineId}`, { method: 'PATCH', body }),
    deleteLine: (creditId: string, lineId: string) =>
      apiFetch<void>(`/api/v1/credits/${creditId}/lines/${lineId}`, { method: 'DELETE' }),
    addFunding: (creditId: string, body: ApiCreateCreditFundingBody) =>
      apiFetch<ApiCreditMemoFunding>(`/api/v1/credits/${creditId}/funding`, { method: 'POST', body }),
    removeFunding: (creditId: string, fundingId: string) =>
      apiFetch<void>(`/api/v1/credits/${creditId}/funding/${fundingId}`, { method: 'DELETE' }),
    addReplacements: (creditId: string, body: { bomLineItemIds: string[]; notes?: string }) =>
      apiFetch<ApiCreditMemoReplacement[]>(`/api/v1/credits/${creditId}/replacements`, { method: 'POST', body }),
    removeReplacement: (creditId: string, bomLineItemId: string) =>
      apiFetch<void>(`/api/v1/credits/${creditId}/replacements/${bomLineItemId}`, { method: 'DELETE' }),
    convertToCo: (creditId: string, body: ApiConvertCreditToCoBody) =>
      apiFetch<ApiChangeOrderDetail>(`/api/v1/credits/${creditId}/convert-to-co`, { method: 'POST', body }),
  },

  googleDrive: {
    listFiles: (params: { folderId?: string; q?: string; pageToken?: string } = {}) => {
      const qs = new URLSearchParams()
      if (params.folderId) qs.set('folderId', params.folderId)
      if (params.q) qs.set('q', params.q)
      if (params.pageToken) qs.set('pageToken', params.pageToken)
      return apiFetch<ApiDriveFileList>(`/api/v1/google-drive/files?${qs}`)
    },
    getFile: (fileId: string) =>
      apiFetch<ApiDriveFile>(`/api/v1/google-drive/files/${fileId}`),
    search: (query: string, mimeType?: string) => {
      const qs = new URLSearchParams({ query })
      if (mimeType) qs.set('mimeType', mimeType)
      return apiFetch<{ files: ApiDriveFile[] }>(`/api/v1/google-drive/search?${qs}`)
    },
    projectFolder: (projectId: string) =>
      apiFetch<{ folderId: string; folderName: string; files: ApiDriveFile[] }>(`/api/v1/google-drive/project/${projectId}/folder`),
    projectPos: (projectId: string) =>
      apiFetch<{ folderId: string | null; files: ApiDrivePoFile[] }>(`/api/v1/google-drive/project/${projectId}/pos`),
    previewImportPo: (projectId: string, fileId: string) =>
      apiFetch<ApiPoImportPreview>(`/api/v1/google-drive/project/${projectId}/preview-import-po`, { method: 'POST', body: { fileId } }),
    importPo: (projectId: string, fileId: string, priceDecisions?: { bomLineItemId: string; usePrice: 'po' | 'bom' }[]) =>
      apiFetch<ApiDriveImportResult>(`/api/v1/google-drive/project/${projectId}/import-po`, { method: 'POST', body: { fileId, priceDecisions } }),
    linkFolder: (projectId: string, folderId: string) =>
      apiFetch<{ success: boolean }>(`/api/v1/google-drive/project/${projectId}/link`, { method: 'POST', body: { folderId } }),
    linkOdSheet: (projectId: string, sheetId: string) =>
      apiFetch<{ success: boolean }>(`/api/v1/google-drive/project/${projectId}/link-od`, { method: 'POST', body: { sheetId } }),
    odCompare: (projectId: string) =>
      apiFetch<ApiOdCompareResult>(`/api/v1/google-drive/project/${projectId}/od-compare`),
  },

  gmail: {
    status: () => apiFetch<ApiGmailStatus>('/api/v1/gmail/status'),
    inbox: (q: { status?: string; limit?: number } = {}) =>
      apiFetch<ApiInboundEmail[]>('/api/v1/gmail/inbox', {
        query: {
          status: q.status ?? 'PENDING',
          ...(q.limit ? { limit: String(q.limit) } : {}),
        },
      }),
    getEmail: (id: string) => apiFetch<ApiInboundEmail>(`/api/v1/gmail/inbox/${id}`),
    processEmail: (id: string, body: { poId?: string; reclassify?: string } = {}) =>
      apiFetch<ApiGmailProcessResult>(`/api/v1/gmail/inbox/${id}/process`, { method: 'POST', body }),
    ignoreEmail: (id: string) =>
      apiFetch<ApiInboundEmail>(`/api/v1/gmail/inbox/${id}/ignore`, { method: 'POST' }),
    retryClassify: (id: string) =>
      apiFetch<ApiInboundEmail>(`/api/v1/gmail/inbox/${id}/retry`, { method: 'POST' }),
    getActions: (id: string) =>
      apiFetch<ApiEmailActionsResponse>(`/api/v1/gmail/inbox/${id}/actions`),
    dispatchAction: (id: string, body: { type: string; poId?: string; params?: Record<string, any> }) =>
      apiFetch<ApiEmailDispatchResult>(`/api/v1/gmail/inbox/${id}/actions`, { method: 'POST', body }),
    getRaw: (id: string) => apiFetch<ApiEmailRaw>(`/api/v1/gmail/inbox/${id}/raw`),
  },

  pdfIntel: {
    uploadInvoice: (projectId: string, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<ApiPdfExtraction>(`/api/v1/pdf-intel/invoice/upload?projectId=${encodeURIComponent(projectId)}`, {
        method: 'POST',
        body: form as any,
        headers: {},
      })
    },
    // Two-phase upload: returns as soon as the row exists, then the caller
    // subscribes to /pdf-intel/:id/stream for live progress. The extraction
    // keeps running server-side even if the client goes away.
    uploadInvoiceStreaming: (projectId: string, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<{ id: string }>(`/api/v1/pdf-intel/invoice/upload/stream?projectId=${encodeURIComponent(projectId)}`, {
        method: 'POST',
        body: form as any,
        headers: {},
      })
    },
    uploadPackingList: (projectId: string, file: File, poId?: string) => {
      const form = new FormData()
      form.append('file', file)
      const qs = new URLSearchParams({ projectId })
      if (poId) qs.set('poId', poId)
      return apiFetch<ApiPdfExtraction>(`/api/v1/pdf-intel/packing-list/upload?${qs}`, {
        method: 'POST',
        body: form as any,
        headers: {},
      })
    },
    // G4/G5: streaming counterpart of uploadPackingList — same two-phase
    // endpoint uploadInvoiceStreaming uses (?kind=PACKING_LIST), but accepts
    // several files in one request (a slip is often photographed as 2-3
    // pages) — see readMultipartDocuments in routes/pdf-intel.ts. Returns
    // immediately with `{ id }`; the caller subscribes to
    // GET /pdf-intel/:id/stream (useExtractionStream) for live progress. Used
    // by Warehouse Mode's Check-in "Capture packing list" local-device
    // fallback (no paired phone) — the paired-phone path instead rides
    // POST /pair/capture/photos, which drives this same extraction server-side.
    uploadPackingListStreaming: (projectId: string, files: File[], poId?: string) => {
      const form = new FormData()
      for (const f of files) form.append('files', f)
      const qs = new URLSearchParams({ projectId, kind: 'PACKING_LIST' })
      if (poId) qs.set('poId', poId)
      return apiFetch<{ id: string }>(`/api/v1/pdf-intel/invoice/upload/stream?${qs}`, {
        method: 'POST',
        body: form as any,
        headers: {},
      })
    },
    fromDrive: (body: {
      projectId: string
      poId?: string
      driveFileId: string
      filename: string
      type: 'QUOTE' | 'INVOICE' | 'PACKING_LIST'
    }) => apiFetch<ApiPdfExtraction>('/api/v1/pdf-intel/drive', { method: 'POST', body }),
    listForProject: (projectId: string) =>
      apiFetch<ApiPdfExtraction[]>(`/api/v1/pdf-intel/project/${projectId}`),
    get: (id: string) => apiFetch<ApiPdfExtraction>(`/api/v1/pdf-intel/${id}`),
    updateLine: (extractionId: string, lineId: string, body: Partial<ApiPdfExtractionLine>) =>
      apiFetch<ApiPdfExtractionLine>(`/api/v1/pdf-intel/${extractionId}/lines/${lineId}`, { method: 'PATCH', body }),
    // F2 (a): replace the full set of BOM links for one quote line — a quote
    // line can price several BOM lines, each with its own quantity.
    updateBomLinks: (extractionId: string, lineId: string, links: { bomLineItemId: string; quantity: number }[]) =>
      apiFetch<ApiPdfExtractionLine>(`/api/v1/pdf-intel/${extractionId}/lines/${lineId}/bom-links`, {
        method: 'PUT',
        body: { links },
      }),
    commitPo: (extractionId: string, body: { vendorId?: string; expectedDate?: string; notes?: string; series?: 100 | 200 | 300 | 400 | 500 }) =>
      apiFetch<any>(`/api/v1/pdf-intel/${extractionId}/commit/po`, { method: 'POST', body }),
    // G4: attach a PACKING_LIST extraction to a PO — the user's pick from
    // poRanking's candidates, or an override of an already auto-selected
    // poMatch/autoSelected. Re-matches every line against the PO's
    // outstanding lines server-side; `autoConfirmedCount` reports how many
    // landed at confidence 1.0 for the confirm-view UI to summarize.
    selectPo: (extractionId: string, poId: string) =>
      apiFetch<ApiPdfExtraction & { autoConfirmedCount: number }>(`/api/v1/pdf-intel/${extractionId}/select-po`, {
        method: 'POST',
        body: { poId },
      }),
    commitReceiving: (extractionId: string, body: {
      poId: string
      location?: string
      // R7: default shelf for every line, plus a per-line override keyed by extraction line id.
      shelfId?: string
      lineShelfOverrides?: Record<string, string>
    }) =>
      apiFetch<any>(`/api/v1/pdf-intel/${extractionId}/commit/receiving`, { method: 'POST', body }),
    // F2 (b/T4/T5): commit reconciled quote prices onto BOM line unitCost.
    commitPrices: (extractionId: string, body: { changeRequestId?: string } = {}) =>
      apiFetch<ApiPdfPriceCommitResult>(`/api/v1/pdf-intel/${extractionId}/commit/prices`, { method: 'POST', body }),
    // Destructive — gated to WRITER_ROLES server-side. If the extraction has
    // committed lines the server 409s with { error: 'HAS_COMMITTED_LINES',
    // committedCount, totalLines } unless force: true is passed.
    delete: (id: string, opts: { force?: boolean } = {}) =>
      apiFetch<void>(`/api/v1/pdf-intel/${id}`, {
        method: 'DELETE',
        query: opts.force ? { force: 'true' } : undefined,
      }),
  },

  estimates: {
    // Core CRUD
    list: (q: Record<string, string> = {}) => apiFetch<any[]>('/api/v1/estimates', { query: q }),
    get: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}`),
    create: (body: { hubspotDealId: string; name?: string }) => apiFetch<any>('/api/v1/estimates', { method: 'POST', body }),
    update: (id: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}`, { method: 'PATCH', body }),
    delete: (id: string) => apiFetch<void>(`/api/v1/estimates/${id}`, { method: 'DELETE' }),
    createRevision: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/revisions`, { method: 'POST' }),

    // Transcripts
    uploadTranscript: (id: string, file: File) => {
      const form = new FormData(); form.append('file', file)
      return apiFetch<any>(`/api/v1/estimates/${id}/transcripts`, { method: 'POST', body: form as any, headers: {} })
    },
    pasteTranscript: (id: string, text: string) => apiFetch<any>(`/api/v1/estimates/${id}/transcripts/paste`, { method: 'POST', body: { text } }),
    deleteTranscript: (id: string, transcriptId: string) => apiFetch<void>(`/api/v1/estimates/${id}/transcripts/${transcriptId}`, { method: 'DELETE' }),
    updateCommentary: (id: string, text: string) => apiFetch<any>(`/api/v1/estimates/${id}/commentary`, { method: 'PATCH', body: { text } }),

    // Materials & Services
    getMs: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/ms`),
    updateMs: (id: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/ms`, { method: 'PATCH', body }),

    // Requirements
    extractRequirements: (id: string) =>
      apiFetch<{ jobId: string; status: string; reattached: boolean }>(
        `/api/v1/estimates/${id}/extract-requirements`,
        { method: 'POST' },
      ),
    listRequirements: (id: string) => apiFetch<any[]>(`/api/v1/estimates/${id}/requirements`),
    createRequirement: (id: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/requirements`, { method: 'POST', body }),
    updateRequirement: (id: string, reqId: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/requirements/${reqId}`, { method: 'PATCH', body }),
    deleteRequirement: (id: string, reqId: string) => apiFetch<void>(`/api/v1/estimates/${id}/requirements/${reqId}`, { method: 'DELETE' }),

    // Build
    build: (id: string, body?: { forceModel?: 'sonnet' | 'opus' }) =>
      apiFetch<{ jobId: string; status: string; reattached: boolean }>(
        `/api/v1/estimates/${id}/build`,
        { method: 'POST', body: body ?? {} },
      ),

    // Instances
    updateInstance: (id: string, instanceId: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/instances/${instanceId}`, { method: 'PATCH', body }),
    createInstance: (id: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/instances`, { method: 'POST', body }),
    resetInstance: (id: string, instanceId: string) => apiFetch<any>(`/api/v1/estimates/${id}/instances/${instanceId}/reset-to-engine`, { method: 'POST' }),
    deleteInstance: (id: string, instanceId: string) => apiFetch<void>(`/api/v1/estimates/${id}/instances/${instanceId}`, { method: 'DELETE' }),
    reorderInstances: (id: string, updates: Array<{ id: string; sortOrder?: number; physicalLocationId?: string; systemId?: string | null }>) =>
      apiFetch<void>(`/api/v1/estimates/${id}/instances/reorder`, { method: 'POST', body: { updates } }),

    // Rooms
    createRoom: (id: string, body: { name: string }) => apiFetch<any>(`/api/v1/estimates/${id}/rooms`, { method: 'POST', body }),
    updateRoom: (id: string, roomId: string, body: { name: string }) => apiFetch<any>(`/api/v1/estimates/${id}/rooms/${roomId}`, { method: 'PATCH', body }),
    deleteRoom: (id: string, roomId: string) => apiFetch<void>(`/api/v1/estimates/${id}/rooms/${roomId}`, { method: 'DELETE' }),
    mergeRoom:  (id: string, roomId: string, targetRoomId: string) => apiFetch<void>(`/api/v1/estimates/${id}/rooms/${roomId}/merge-into`, { method: 'POST', body: { targetRoomId } }),

    // Systems
    createSystem: (id: string, body: { name: string }) => apiFetch<any>(`/api/v1/estimates/${id}/systems`, { method: 'POST', body }),
    updateSystem: (id: string, systemId: string, body: { name: string }) => apiFetch<any>(`/api/v1/estimates/${id}/systems/${systemId}`, { method: 'PATCH', body }),
    deleteSystem: (id: string, systemId: string) => apiFetch<void>(`/api/v1/estimates/${id}/systems/${systemId}`, { method: 'DELETE' }),

    // Scenarios
    createScenario: (id: string, body: { sourceScenariosId: string; name?: string }) => apiFetch<any>(`/api/v1/estimates/${id}/scenarios`, { method: 'POST', body }),
    updateScenario: (id: string, scenarioId: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/scenarios/${scenarioId}`, { method: 'PATCH', body }),
    resetScenario: (id: string, scenarioId: string) => apiFetch<any>(`/api/v1/estimates/${id}/scenarios/${scenarioId}/reset-from-selected`, { method: 'POST' }),
    deleteScenario: (id: string, scenarioId: string) => apiFetch<void>(`/api/v1/estimates/${id}/scenarios/${scenarioId}`, { method: 'DELETE' }),
    diffScenarios: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/scenarios/diff`),

    // Narrative
    updateNarrative: (id: string, text: string) => apiFetch<any>(`/api/v1/estimates/${id}/narrative`, { method: 'PATCH', body: { text } }),

    // Diagrams
    uploadDiagram: (id: string, file: File) => {
      const form = new FormData(); form.append('file', file)
      return apiFetch<any>(`/api/v1/estimates/${id}/diagrams`, { method: 'POST', body: form as any, headers: {} })
    },
    updateDiagram: (id: string, diagramId: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/diagrams/${diagramId}`, { method: 'PATCH', body }),
    deleteDiagram: (id: string, diagramId: string) => apiFetch<void>(`/api/v1/estimates/${id}/diagrams/${diagramId}`, { method: 'DELETE' }),

    // Adders
    addAdder: (id: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/adders`, { method: 'POST', body }),
    updateAdder: (id: string, adderId: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/adders/${adderId}`, { method: 'PATCH', body }),
    deleteAdder: (id: string, adderId: string) => apiFetch<void>(`/api/v1/estimates/${id}/adders/${adderId}`, { method: 'DELETE' }),

    // Approval workflow
    submitForApproval: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/submit-for-approval`, { method: 'POST' }),
    approve: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/approve`, { method: 'POST' }),
    reject: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/reject`, { method: 'POST' }),
    publish: (id: string, body: any) => apiFetch<any>(`/api/v1/estimates/${id}/publish`, { method: 'POST', body }),
    accept: (id: string) => apiFetch<any>(`/api/v1/estimates/${id}/accept`, { method: 'POST' }),
  },

  knowledgeBase: {
    listSubsystems: (q: Record<string, string> = {}) => apiFetch<any[]>('/api/v1/kb/subsystems', { query: q }),
    getSubsystem: (id: string) => apiFetch<any>(`/api/v1/kb/subsystems/${id}`),
    createSubsystem: (body: any) => apiFetch<any>('/api/v1/kb/subsystems', { method: 'POST', body }),
    updateSubsystem: (id: string, body: any) => apiFetch<any>(`/api/v1/kb/subsystems/${id}`, { method: 'PATCH', body }),
    deleteSubsystem: (id: string) => apiFetch<void>(`/api/v1/kb/subsystems/${id}`, { method: 'DELETE' }),

    interpretContributionRule: (subsystemId: string, body: { targetSubsystemId: string; description: string }) =>
      apiFetch<{
        targetProperty: string
        amount: string
        direction: 'CONTRIBUTES' | 'CONSUMES'
        rationale: string
        formulaError?: string
        usage: { inputTokens: number; outputTokens: number; costUsd: number; model: string }
      }>(`/api/v1/kb/subsystems/${subsystemId}/interpret-rule`, { method: 'POST', body }),

    suggestSubsystemConsiderations: (id: string) =>
      apiFetch<{ considerations: string[]; usage: { inputTokens: number; outputTokens: number; costUsd: number; model: string } }>(
        `/api/v1/kb/subsystems/${id}/suggest-considerations`,
        { method: 'POST' },
      ),

    addTier: (subsystemId: string, body: any) => apiFetch<any>(`/api/v1/kb/subsystems/${subsystemId}/tiers`, { method: 'POST', body }),
    updateTier: (tierId: string, body: any) => apiFetch<any>(`/api/v1/kb/tiers/${tierId}`, { method: 'PATCH', body }),

    addContributionRule: (body: any) => apiFetch<any>('/api/v1/kb/contribution-rules', { method: 'POST', body }),
    updateContributionRule: (ruleId: string, body: any) => apiFetch<any>(`/api/v1/kb/contribution-rules/${ruleId}`, { method: 'PATCH', body }),
    deleteContributionRule: (ruleId: string) => apiFetch<void>(`/api/v1/kb/contribution-rules/${ruleId}`, { method: 'DELETE' }),

    listTags: () => apiFetch<any[]>('/api/v1/kb/tags'),
    createTag: (body: any) => apiFetch<any>('/api/v1/kb/tags', { method: 'POST', body }),

    getGraph: () => apiFetch<any>('/api/v1/kb/graph'),

    // ── Stats (token estimates) ───────────────────────────────────────────────
    getStats: () => apiFetch<{
      ratioCharsPerToken: number
      promptSections: { count: number; chars: number; tokens: number; breakdown: { slug: string; title: string; chars: number; tokens: number }[] }
      kbSnapshot: { subsystemCount: number; tierCount: number; ruleCount: number; chars: number; tokens: number }
      total: { chars: number; tokens: number }
      cost: {
        pricing: {
          model: string
          contextWindow: number
          longContextThreshold: number
          inputPerMTok: number; outputPerMTok: number; cacheWritePerMTok: number; cacheReadPerMTok: number
          longInputPerMTok: number; longOutputPerMTok: number; longCacheWritePerMTok: number; longCacheReadPerMTok: number
        }
        assumptions: { userMessageTokens: number; outputTokens: number; note: string }
        tier: 'standard' | 'long'
        totalInputTokens: number
        longTierThreshold: number
        cold: { cacheableCost: number; userMsgCost: number; outputCost: number; totalCost: number }
        warm: { cacheableCost: number; userMsgCost: number; outputCost: number; totalCost: number }
        savedPerWarmCall: number
      }
    }>('/api/v1/kb/stats'),

    // ── Prompt sections (judgment-layer) ──────────────────────────────────────
    listPromptSections: () => apiFetch<any[]>('/api/v1/kb/prompt-sections'),
    getPromptSection: (slug: string) => apiFetch<any>(`/api/v1/kb/prompt-sections/${slug}`),
    createPromptSection: (body: { slug: string; title: string; body?: string; ordering?: number; isActive?: boolean }) =>
      apiFetch<any>('/api/v1/kb/prompt-sections', { method: 'POST', body }),
    updatePromptSection: (slug: string, body: { title?: string; body?: string; ordering?: number; isActive?: boolean }) =>
      apiFetch<any>(`/api/v1/kb/prompt-sections/${slug}`, { method: 'PATCH', body }),
    deletePromptSection: (slug: string) =>
      apiFetch<void>(`/api/v1/kb/prompt-sections/${slug}`, { method: 'DELETE' }),

    // ── KB bundles (import/export) ────────────────────────────────────────────
    exportBundle: () => apiFetch<any>('/api/v1/kb/bundle/export'),
    previewBundle: (bundle: unknown) =>
      apiFetch<{
        previewId: string
        envelope: {
          version: number
          exportedAt: string | null
          exportedFromEnv: string | null
          subsystemCount: number
          tagCount: number
          promptSectionCount: number
        }
        diff: {
          tags:            { added: string[]; updated: string[] }
          promptSections:  { added: string[]; updated: string[] }
          subsystems:      { added: string[]; updated: string[] }
          tiers:           { added: number; updated: number }
          rules:           { added: number; updated: number }
          conflicts: string[]
        }
        canApply: boolean
      }>('/api/v1/kb/bundle/preview', { method: 'POST', body: bundle }),
    applyBundle: (previewId: string) =>
      apiFetch<{ importId: string; counts: any }>('/api/v1/kb/bundle/apply', { method: 'POST', body: { previewId } }),
    listBundleImports: () => apiFetch<any[]>('/api/v1/kb/bundle/imports'),
  },

  hubspot: {
    searchDeals: (q: string) => apiFetch<any[]>('/api/v1/estimates/hubspot/deals', { query: { q } }),
  },

  aiJobs: {
    get: (id: string) => apiFetch<AiJob>(`/api/v1/ai-jobs/${id}`),
    list: (q: { targetType: string; targetId: string; kind?: string; status?: string; limit?: number }) =>
      apiFetch<AiJob[]>('/api/v1/ai-jobs', { query: q as any }),
    cancel: (id: string) => apiFetch<AiJob>(`/api/v1/ai-jobs/${id}/cancel`, { method: 'POST' }),
  },

  // ─── Asset tracking (Teltonika TAT141) ──────────────────────────────────────
  // Contract per docs/specs/asset-tracking-tat141.md §7.1. The backend does not
  // exist yet; useTracking.ts currently resolves these from fixtures. Swapping to
  // live data is one line per composable — see the TODO markers there.
  tracking: {
    // M5 G5 (company assets): optional currentProjectId filter — same list endpoint
    // backs the fleet view and a project's read-only "Company assets on this job" list.
    listAssets: (q: Record<string, string> = {}) =>
      apiFetch<ApiTrackedAsset[]>('/api/v1/tracking/assets', { query: q }),
    getAsset: (id: string) => apiFetch<ApiTrackedAsset>(`/api/v1/tracking/assets/${id}`),
    createAsset: (body: ApiUpsertAssetBody) =>
      apiFetch<ApiTrackedAsset>('/api/v1/tracking/assets', { method: 'POST', body }),
    updateAsset: (id: string, body: Partial<ApiUpsertAssetBody>) =>
      apiFetch<ApiTrackedAsset>(`/api/v1/tracking/assets/${id}`, { method: 'PATCH', body }),
    replaceAssetItems: (id: string, items: ApiUpsertAssetItem[]) =>
      apiFetch<ApiTrackedAssetItem[]>(`/api/v1/tracking/assets/${id}/items`, { method: 'PUT', body: { items } }),
    // M5 G5: label print (same ZPL/Zebra path as item labels), check-out/in, and the
    // scan-side movement log (MovementEvent rows) for the asset detail history.
    getAssetLabel: (id: string) =>
      apiFetch<{ zpl: string; assetTag: string }>(`/api/v1/tracking/assets/${id}/label`),
    checkOutAsset: (id: string, body: ApiCheckOutAssetBody) =>
      apiFetch<ApiTrackedAsset>(`/api/v1/tracking/assets/${id}/check-out`, { method: 'POST', body }),
    checkInAsset: (id: string, body: ApiCheckInAssetBody) =>
      apiFetch<ApiTrackedAsset>(`/api/v1/tracking/assets/${id}/check-in`, { method: 'POST', body }),
    assetMovements: (id: string) =>
      apiFetch<ApiMovementEvent[]>(`/api/v1/tracking/assets/${id}/movements`),

    listDevices: () => apiFetch<ApiTrackerDevice[]>('/api/v1/tracking/devices'),
    createDevice: (body: ApiCreateDeviceBody) =>
      apiFetch<ApiTrackerDevice>('/api/v1/tracking/devices', { method: 'POST', body }),
    getDevice: (id: string) => apiFetch<ApiTrackerDevice>(`/api/v1/tracking/devices/${id}`),
    updateDevice: (id: string, body: ApiUpdateDeviceBody) =>
      apiFetch<ApiTrackerDevice>(`/api/v1/tracking/devices/${id}`, { method: 'PATCH', body }),

    /** Recent trails for every device in one call — the fleet overview map. */
    tracks: (q: Record<string, string> = {}) =>
      apiFetch<ApiTrackerTrack[]>('/api/v1/tracking/tracks', { query: q }),
    positions: (deviceId: string, q: Record<string, string> = {}) =>
      apiFetch<ApiTrackerPosition[]>(`/api/v1/tracking/devices/${deviceId}/positions`, { query: q }),
    events: (deviceId: string, q: Record<string, string> = {}) =>
      apiFetch<ApiTrackerEvent[]>(`/api/v1/tracking/devices/${deviceId}/events`, { query: q }),
    health: () => apiFetch<ApiTrackerHealth>('/api/v1/tracking/health'),
  },
}

export interface AiJob {
  id: string
  kind: 'EST_EXTRACT' | 'EST_GENERATE' | 'KB_NARRATIVE_DRAFT' | 'KB_RULE_INTERPRET' | 'KB_SUBSYSTEM_CONSIDERATIONS'
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
  targetType: string
  targetId: string
  model: string | null
  startedAt: string | null
  completedAt: string | null
  errorCode: 'TIMEOUT' | 'MAX_TOKENS' | 'SCHEMA' | 'UPSTREAM' | 'INTERNAL' | 'CANCELLED' | null
  errorMessage: string | null
  result: any | null
  inputTokens: number | null
  outputTokens: number | null
  costUsd: number | null
  progressText: string | null
  createdById: string
  createdAt: string
  updatedAt: string
  createdBy?: { id: string; name: string; email?: string }
}

// ─── Asset tracking (Teltonika TAT141) ────────────────────────────────────────
// Mirrors the Prisma models in docs/specs/asset-tracking-tat141.md §7.

export type ApiTrackerFixSource = 'GPS' | 'CELL' | 'NONE'
export type ApiTrackerMotion = 'STOPPED' | 'MOVING' | 'UNKNOWN'
export type ApiTrackedAssetKind = 'CASE' | 'VEHICLE' | 'TOOL' | 'OTHER'

/** Device telemetry event. `kind` is a free-form verb, matching the AuditLog idiom. */
export interface ApiTrackerEvent {
  id: string
  deviceId: string
  occurredAt: string
  kind:
    | 'MOVE_START' | 'MOVE_STOP' | 'ALARM' | 'RECOVERY' | 'BLE_LOST'
    | 'CONNECT' | 'REJECTED' | 'ASSET_ASSIGNED' | 'ASSET_UNASSIGNED'
    | 'WAREHOUSE_ARRIVED' | 'WAREHOUSE_DEPARTED'
  detail: Record<string, unknown> | null
}

export interface ApiTrackerPosition {
  id: string
  deviceId: string
  /** Device clock, UTC. May be far older than receivedAt after a buffered backlog flush. */
  recordedAt: string
  receivedAt: string
  priority: number
  eventIoId: number

  // GPS element exactly as sent. STALE unless isFreshFix — the device repeats the
  // last valid fix when it has none. See spec §5.1.
  latitude: number | null
  longitude: number | null
  altitude: number | null
  angle: number | null
  satellites: number
  speedKph: number

  fixSource: ApiTrackerFixSource
  /** IO 69 verbatim: 0 off, 1 on-with-fix, 2 on-no-fix, 3 sleep, 4 static-nav. */
  gnssStatus: number
  /** gnssStatus === 1. The only trustworthy freshness signal. */
  isFreshFix: boolean
  hdop: number | null
  pdop: number | null

  // Raw cell identifiers — retained regardless of geolocation provider.
  mcc: number | null
  mnc: number | null
  tac: number | null
  cellId: number | null
  rsrp: number | null
  rsrq: number | null

  // Populated only when fixSource === 'CELL'.
  resolvedLat: number | null
  resolvedLng: number | null
  accuracyMeters: number | null
  geoProvider: string | null
  geoResolvedAt: string | null

  /** Warehouse geofence key this fix matched, if any. See spec §8. */
  geofenceKey: string | null
  /** Reverse-geocoded "City, ST", resolved server-side at poll time. */
  placeLabel: string | null

  motion: ApiTrackerMotion
  /** Battery voltage in millivolts (IO 67). There is no battery-percentage element. */
  batteryMv: number | null
  /** IO 21 — a 1–5 bar scale, not dBm. */
  gsmSignal: number | null
}

export interface ApiTrackerDevice {
  id: string
  imei: string
  label: string
  enabled: boolean
  model: string
  firmware: string | null
  iccid: string | null
  notes: string | null
  assetId: string | null
  /** Device param 186, seconds. Mirrored so the UI can compute an overdue threshold. */
  onMovePeriodS: number | null
  /** Device param 185, seconds. */
  onStopPeriodS: number | null
  lastSeenAt: string | null
  /** Last record with gnssStatus === 1. */
  lastFixAt: string | null
  batteryMv: number | null
  /** Warehouse geofence key of the most recent matched fix, if any. */
  lastGeofenceKey: string | null
}

/** One asset's recent trail, as returned by the fleet overview endpoint. */
export interface ApiTrackerTrack {
  assetId: string
  assetName: string
  deviceId: string
  positions: ApiTrackerPosition[]
}

export interface ApiTrackedAssetItem {
  id: string
  name: string
  quantity: number
  serial: string | null
  notes: string | null
}

// M5 G4/G5 (company assets, docs/specs/warehouse-m5-stock-sortly-assets.md #10) — the
// two physical warehouses an asset can call home. Distinct from LocationState, which
// also covers transit/jobsite states a home never is.
export type ApiAssetHomeLocation = 'OKC' | 'ATL'

export interface ApiTrackedAsset {
  id: string
  name: string
  kind: ApiTrackedAssetKind
  description: string | null
  active: boolean
  device: ApiTrackerDevice | null
  latestPosition: ApiTrackerPosition | null
  items: ApiTrackedAssetItem[]
  // M5 G4: warehouse-side fields. Never read by BOM/GP/credit/project-report code —
  // this record is not a TrackedItem.
  assetTag: string
  homeLocation: ApiAssetHomeLocation
  location: LocationState
  currentProjectId: string | null
  currentProject: { id: string; code: string; name: string } | null
  checkedOutAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiUpsertAssetItem {
  name: string
  quantity: number
  serial?: string | null
  notes?: string | null
}

export interface ApiUpsertAssetBody {
  name: string
  kind: ApiTrackedAssetKind
  description?: string | null
  active?: boolean
  homeLocation?: ApiAssetHomeLocation
}

export interface ApiCheckOutAssetBody {
  projectId: string
  shipmentId?: string
}

export interface ApiCheckInAssetBody {
  location: LocationState
}

/** MovementEvent row, as returned by GET /tracking/assets/:id/movements (M5 G5) — the
 *  scan-side half of an asset's history, merged with GPS positions in AssetDetailView. */
export interface ApiMovementEvent {
  id: string
  trackedItemId: string | null
  containerId: string | null
  trackedAssetId: string | null
  fromLocation: LocationState
  toLocation: LocationState
  transitType: string | null
  carrierTracking: string | null
  shipmentId: string | null
  movedById: string | null
  notes: string | null
  createdAt: string
}

export interface ApiCreateDeviceBody {
  imei: string
  label: string
  enabled?: boolean
  notes?: string | null
  assetId?: string | null
  onMovePeriodS?: number | null
  onStopPeriodS?: number | null
}

export interface ApiUpdateDeviceBody {
  label?: string
  enabled?: boolean
  notes?: string | null
  assetId?: string | null
}

export interface ApiTrackerHealth {
  listening: boolean
  host: string | null
  port: number | null
  lastPacketAt: string | null
  packetsAccepted: number
  packetsRejected: number
  acksSent: number
}

// ─── API response types (Prisma shapes) ───────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  location?: string
  avatarUrl?: string
}

export interface ApiProject {
  id: string
  code: string
  name: string
  revenueStream: string
  status: string
  phase: string | null
  location: string
  color: 'YELLOW' | 'GOLD' | 'GREEN' | 'BROWN' | 'BLACK' | 'PINK' | 'NEON_ORANGE' | 'BLUE' | 'LIGHT_ORANGE' | 'RED' | 'CYAN' | 'WHITE' | null
  contractTotal: number
  targetCompletion: string | null
  createdAt: string
  // Present on every GET /projects and GET /projects/:id payload (Prisma @updatedAt column,
  // not select-restricted) but wasn't typed here — ProjectPicker's recency ranking needs it.
  updatedAt: string
  hubspotId: string | null
  basecampId: string | null
  jetbuiltId: string | null
  vwActive: boolean
  account: { id: string; name: string } | null
  pm: { id: string; name: string } | null
  leadEngineer: { id: string; name: string } | null
  invoices: ApiInvoiceSummary[]
  payments: ApiPaymentSummary[]
  changeOrders: ApiChangeOrderSummary[]
  _count?: { purchaseOrders: number; invoices: number }
}

export interface ApiInvoiceSummary {
  id: string
  label: string
  amount: number
  dueDate: string | null
  status: string
  updatedAt: string
}

// Full invoice row as returned inside the single-project detail GET.
export interface ApiProjectInvoice extends ApiInvoiceSummary {
  invoiceDate: string | null
  terms: string | null
  notes: string | null
  changeOrderId: string | null
  kind: 'STANDARD' | 'CLIENT_CREDIT'
}

export interface ApiPaymentSummary {
  amount: number
  receivedAt: string | null
  purchaseOrderId?: string | null
}

export interface ApiChangeOrderSummary {
  id: string
  number: number
  description: string
  costImpact: number
  status: string
  source: 'JETBUILT' | 'MANUAL'
  createdAt: string
}

export interface ApiProjectDetail extends ApiProject {
  notes: string | null
  milestones: any[]
  bom: ApiBomVersion[]
  purchaseOrders: ApiPO[]
  // Detail GET returns full Invoice rows (list GET returns the summary subset).
  invoices: ApiProjectInvoice[]
  payments: any[]
  trips: any[]
  changeRequests?: ApiChangeRequest[]
  // Server-derived: cost above baseline carried by lines tagged to active CRs.
  // Created-under-CR lines contribute their full totalCost; edited-under-CR lines
  // contribute (current - originalTotalCost). Cancelled CRs are excluded.
  crCostBurden?: number
  // Sum of CreditMemoFunding rows targeting active CRs — offsets crCostBurden 1:1.
  crCreditOffset?: number
  // O4: subset of crCostBurden carried by CRs classified as engineering-impacting
  // (reason DESIGN_REFINEMENT_SAME_SCOPE / EXECUTION_NECESSITY) vs commercial
  // (MISSED_IN_ESTIMATE / MISSED_IN_PROPOSAL / PRICE_INCREASE).
  crEngineeringBurden?: number
  crEngineeringCreditOffset?: number
}

export type ApiChangeRequestReason =
  | 'MISSED_IN_ESTIMATE'
  | 'MISSED_IN_PROPOSAL'
  | 'DESIGN_REFINEMENT_SAME_SCOPE'
  | 'EXECUTION_NECESSITY'
  | 'PRICE_INCREASE'

export type ApiChangeRequestStatus = 'ACTIVE' | 'CANCELLED'

export interface ApiChangeRequest {
  id: string
  projectId: string
  reason: ApiChangeRequestReason
  title: string
  notes: string | null
  status: ApiChangeRequestStatus
  createdById: string
  createdBy: { id: string; name: string; email: string }
  createdAt: string
  cancelledAt: string | null
  cancelledById: string | null
  cancelledBy: { id: string; name: string; email: string } | null
}

export interface ApiCreateChangeRequestBody {
  reason: ApiChangeRequestReason
  title: string
  notes?: string
}

export interface ApiUpdateChangeRequestBody {
  title?: string
  notes?: string | null
  reason?: ApiChangeRequestReason
}

// Returned by POST /change-requests/:id/cancel — counts of audit-walk reverts so the UI
// can show a meaningful confirmation summary after the operation completes.
export interface ApiChangeRequestRevertSummary {
  deletedCreates: number          // hard-deleted lines that were created under this CR
  softDeletedCreates: number      // created lines that had FK refs and got soft-deleted instead
  revertedUpdates: number
  restoredSoftDeletes: number
  skipped: number
}

export interface ApiBomVersion {
  id: string
  revision: number
  approved: boolean
  approvedAt: string | null
  lineItems: ApiBomLineItem[]
}

// ─── M3 G2: on-site access (docs/specs/warehouse-m3-onsite.md) ────────────────

export interface ApiOnsiteCode {
  id: string
  projectId: string
  code: string
  createdById: string | null
  createdAt: string
  revokedAt: string | null
}

// Session shape as selected by GET /:projectId/onsite (routes/onsite-staff.ts) —
// no `ip`, deliberately: staff see device + last seen, not the guest's address.
export interface ApiOnsiteSession {
  id: string
  guestName: string
  userAgent: string | null
  createdAt: string
  lastSeenAt: string
}

export interface ApiOnsiteOverview {
  code: ApiOnsiteCode | null
  sessions: ApiOnsiteSession[]
  openIssueCount: number
}

export type ApiOnsiteIssueKind = 'SHORT' | 'DAMAGED' | 'WRONG_ITEM' | 'OTHER'

export interface ApiOnsiteIssue {
  id: string
  projectId: string
  onsiteSessionId: string | null
  reportedByName: string
  kind: ApiOnsiteIssueKind
  containerId: string | null
  bomLineItemId: string | null
  trackedItemId: string | null
  expectedQty: number | null
  foundQty: number | null
  note: string | null
  createdAt: string
  resolvedAt: string | null
  resolvedById: string | null
  resolutionNote: string | null
}

export interface ApiBomLineItem {
  id: string
  section: string
  // Kept in lockstep with `section` server-side (sectionToType). Contingency fund headers
  // are identified by `type === 'CONTINGENCY' && isBudget` — children keep the type of
  // their OWN section, never their parent's, so a contingency-funded child must be found
  // via budgetLineItemId → parent lookup, not by its own type.
  type: 'EQUIPMENT' | 'MATERIALS' | 'SERVICES' | 'CONTINGENCY'
  room: string | null
  system: string | null
  description: string
  manufacturer: string | null
  model: string | null
  quantity: number
  unitCost: number
  totalCost: number
  unitPrice: number
  totalPrice: number
  gpPercent: number
  gpDollar: number
  procurementStatus: string
  receivingStatus: 'NOT_RECEIVED' | 'PARTIAL' | 'RECEIVED'
  paidStatus: 'UNPAID' | 'PARTIAL' | 'PAID'
  inVectorworks: boolean
  vwInclude: boolean
  vwPlacedQty: number
  vwDeviceNames: string[]
  vwSyncedAt: string | null
  isBudget: boolean
  // O5: only meaningful when isBudget=true. MARGIN (default) = ordinary budget bucket,
  // unspent falls through to GP at close. REFUNDABLE_NTE = client-money placeholder
  // (e.g. travel not-to-exceed) — unspent at close is a refund owed to the client.
  budgetKind: 'MARGIN' | 'REFUNDABLE_NTE'
  budgetLineItemId: string | null
  // When two or more sibling lines share a splitGroupId, they were split out of one
  // user-perceived item (e.g., some units from existing stock at original cost, others
  // ordered fresh at current market cost). Duplicate-line preserves this relationship
  // by stamping the copies with a fresh splitGroupId.
  splitGroupId: string | null
  originalUnitCost: number | null
  originalTotalCost: number | null
  originalUnitPrice: number | null
  originalTotalPrice: number | null
  // SKY-1537: approved-baseline category/membership snapshot. NULL on legacy lines.
  approvedSection: string | null
  approvedBudgetLineItemId: string | null
  deletedAt: string | null
  changeOrderId: string | null
  changeRequestId: string | null
  vwDeviceName: string | null
  sortOrder: number
  notes: string | null
  // #67: Budget closeout. Only meaningful on budget lines (isBudget=true).
  closedAt?: string | null
  closedSpend?: number | null
  closedBy?: { id: string; name: string } | null
  creditLines?: {
    id: string
    creditMemoId: string
    description: string
    creditAmount: number
    quantity: number | null
    creditMemo: { id: string; reference: string; status: string }
  }[]
  fundedByCredits?: {
    creditMemoId: string
    creditMemo: { id: string; reference: string; status: string }
  }[]
  orderedQty: number
  // m2m: BOM line links to PO lines via the join. Server returns poLinks; each
  // entry exposes its PurchaseOrderLine, which carries po.poNumber and the
  // po's shipTo* fields (O3 ship-to column — fed through deriveShipToLocation).
  poLinks?: {
    poLine: {
      po: {
        poNumber: string
        shipToAttn?: string | null
        shipToName?: string | null
        shipToStreet?: string | null
        shipToCity?: string | null
        shipToState?: string | null
        shipToZip?: string | null
      }
    }
  }[]
  // Server attaches this on successful PATCH responses so the client can register
  // a revert target on the undo stack (issue #44). Null when the patch was a
  // no-op and no audit row was created.
  _auditLogId?: string | null
  // True when the PATCH was actually CR-required (quantity change or cost going
  // above baseline). Drives the active-CR timer-reset rule on the frontend.
  _crGated?: boolean
  // F2: only present on the GET /:id project route (not on PATCH responses). Used to
  // derive the "Manual" tag — RECEIVED with zero of each means it was set by hand.
  _count?: { receivingLines: number; trackedItems: number }
}

export interface ApiBomLineTrackedItemMovement {
  id: string
  from: string
  to: string
  transitType: string | null
  carrierTracking: string | null
  notes: string | null
  createdAt: string
}

export interface ApiBomLineTrackedItemEntry {
  id: string
  skylarkId: string
  location: string
  serialNumber: string | null
  warrantyDate: string | null
  container: { id: string; containerId: string; type: string; name: string | null } | null
  shelf: { code: string; location: string } | null
  poNumber: string | null
  movementEvents: ApiBomLineTrackedItemMovement[]
  movementEventCount: number
}

export interface ApiBomLineTrackedItems {
  bomLineId: string
  description: string
  manufacturer: string | null
  model: string | null
  expectedQty: number
  receivedQty: number
  counts: { total: number; byLocation: Record<string, number> }
  items: ApiBomLineTrackedItemEntry[]
}

export interface ApiVwUnmatchedDevice {
  make: string
  model: string
  deviceNames: string[]
  count: number
  syncedAt: string | null
}

export interface ApiVwAiSuggestion {
  vwMake: string
  vwModel: string
  bomLineItemId: string
  reason: string
}

export interface ApiBomLineDeleteImpact {
  lineId: string
  isBudget: boolean
  description: string
  budgetedAmount: number | null
  childrenCount: number
  childrenSummary: { id: string; description: string; unitPrice: number; totalCost: number }[]
  poLinks: { poId: string; poNumber: string; status: string; lineCount: number; hasPayments: boolean; hasReceiving: boolean }[]
  changeOrderRefs: { coId: string; coNumber: number }[]
  hasIssuedPo: boolean
  hasReceivedItems: boolean
  hasPayments: boolean
  blockReasons: string[]
}

// GET .../lines/:lineId/po-impact — preflight for a unitCost edit on a PO-linked line.
// One entry per linked PurchaseOrderLine; siblings are OTHER BOM lines sharing that same
// PO line (multi-room bulk-buys aggregate onto one PO line — see PurchaseOrderLineBomLink).
export interface ApiPoLinePriceImpact {
  poLineId: string
  poId: string
  poNumber: string
  poStatus: string
  currentUnitCost: number
  siblings: { bomLineItemId: string; description: string }[]
}

export interface ApiJbReconcileDiffEntry {
  bomLineId: string
  description: string
  manufacturer: string | null
  model: string | null
  local: { unitCost: number; originalUnitCost: number | null; quantity: number }
  jetbuilt: { unitCost: number; quantity: number }
  proposedNewUnitCost: number
  proposedNewOriginalUnitCost: number
  delta: number
  confidence: 'matched_by_id' | 'matched_by_text' | 'no_match'
}

export interface ApiPO {
  id: string
  poNumber: string
  series: number | null
  status: string
  isDirectPurchase: boolean
  totalAmount: number
  expectedDate: string | null
  orderedDate: string | null
  createdAt: string
  location?: string
  notes: string | null
  quoteNumber: string | null
  paymentMethod: string | null
  paymentMethodDetail: string | null
  shipToAttn: string | null
  shipToName: string | null
  shipToStreet: string | null
  shipToCity: string | null
  shipToState: string | null
  shipToZip: string | null
  shippingMethod: string | null
  paidStatus: 'UNPAID' | 'PARTIAL' | 'PAID'
  prepayAmount: number | null
  paidAmount: number              // derived: sum of recorded payments
  paidStatusIsManual: boolean     // derived: paidStatus was user-overridden vs payment sum
  payments?: ApiPaymentRecord[]
  project: { id: string; code: string; name: string }
  vendor: { id: string; name: string } | null
  lines: ApiPOLine[]
  // F3: only ever set on a series-500 stock PO.
  fulfillFrom?: 'OKC' | 'ATL' | null
  deliverTo?: 'OKC' | 'ATL' | null
}

export interface ApiPaymentRecord {
  id: string
  projectId: string
  invoiceId: string | null
  purchaseOrderId: string | null
  amount: number
  receivedAt: string | null
  bankReference: string | null
  notes: string | null
  createdAt: string
}

export interface ApiPOLine {
  id: string
  lineNumber: number
  description: string
  quantity: number
  unitCost: number
  totalCost: number
  receivedQty: number
  unitOfMeasure: string
}

export interface ApiVendor {
  id: string
  name: string
  contactName?: string | null
  email?: string | null
  phone?: string | null
  paymentTerms?: string | null
  notes?: string | null
  active?: boolean
  is1099?: boolean
  taxId?: string | null
  businessType?: string | null
  createdAt?: string
}

export interface ApiVendor1099ReportRow {
  id: string
  name: string
  contactName: string | null
  email: string | null
  taxId: string | null
  businessType: string | null
  ytdSpend: number
  needs1099: boolean
}

export interface ApiVendor1099Report {
  year: number
  vendors: ApiVendor1099ReportRow[]
  nonContractorsWithHighSpend: { id: string; name: string; ytdSpend: number; is1099: boolean }[]
}

// A PO line as returned by the PO detail endpoint — carries its shipment
// lines (each with the parent shipment's carrier/tracking/dates) and the
// derived unshipped remainder (F1/J5). Per-line ship/arrival dates live on
// the shipment, not the line, since a line can split across shipments.
export interface ApiPoDetailLine extends ApiPOLine {
  unshippedQuantity: number
  shipmentLines: {
    id: string
    quantity: number
    shipment: {
      id: string
      carrierName: string | null
      trackingNumber: string | null
      status: string
      expectedShipDate: string | null
      expectedDelivery: string | null
    }
  }[]
}

export interface ApiPoDetail extends ApiPO {
  lines: ApiPoDetailLine[]
  receivingRecords: {
    id: string
    location: string
    receivedAt: string
    binReference: string | null
    lines: { id: string; poLineId: string; quantityReceived: number; notes: string | null }[]
  }[]
}

export interface ApiInvoice {
  id: string
  label: string
  amount: number
  dueDate: string | null
  issuedAt: string | null
  status: string
  kind: 'STANDARD' | 'CLIENT_CREDIT'
  notes: string | null
  createdAt: string
  updatedAt: string
  project: { id: string; code: string; name: string }
  payments: ApiPaymentSummary[]
  changeOrder: { id: string; number: number } | null
}

export interface ApiCashFlowBucket {
  window: string
  inbound: number
  outbound: number
  net: number
}

export interface ApiApprovalThreshold {
  id: string
  entityType: string
  minAmount: number
  maxAmount: number | null
  requiredRole: string
  requiredCount: number
  active: boolean
  sortOrder: number
}

export interface ApiCreateThresholdBody {
  entityType: 'PurchaseOrder' | 'Invoice' | 'Payment'
  minAmount: number
  maxAmount?: number | null
  requiredRole: string
  requiredCount?: number
  sortOrder?: number
}

export interface ApiPendingApproval {
  id: string
  entityType: string
  decision: string
  notes: string | null
  createdAt: string
  approver: { id: string; name: string }
  po?: { id: string; poNumber: string; totalAmount: number; project: { id: string; code: string; name: string } } | null
  invoice?: { id: string; label: string; amount: number; project: { id: string; code: string; name: string } } | null
  payment?: { id: string; amount: number; project: { id: string; code: string; name: string } } | null
}

export interface ApiFixedCost {
  id: string
  label: string
  amount: number
  active: boolean
  sortOrder: number
}

export interface ApiCashFlow {
  buckets: ApiCashFlowBucket[]
  totalIncomingPayments: number
  arBalance: number
  fixedCosts: ApiFixedCost[]
}

export interface ApiCashFlowDetailInvoice {
  id: string
  label: string
  amount: number
  dueDate: string | null
  status: string
  project: { id: string; code: string; name: string }
}

export interface ApiCashFlowDetailPo {
  id: string
  poNumber: string
  amount: number            // net outstanding = totalAmount - paidAmount
  totalAmount: number       // gross PO value (for context display)
  paidAmount: number
  paidStatus: 'UNPAID' | 'PARTIAL' | 'PAID'
  expectedDate: string | null
  status: string
  vendor: string
  project: { id: string; code: string; name: string }
}

export interface ApiCashFlowDetailBucket {
  window: string
  invoices: ApiCashFlowDetailInvoice[]
  pos: ApiCashFlowDetailPo[]
}

export interface ApiCashFlowOverdueInvoice extends ApiCashFlowDetailInvoice {
  daysOverdue: number
}

export interface ApiCashFlowDetail {
  buckets: ApiCashFlowDetailBucket[]
  overdue: ApiCashFlowOverdueInvoice[]
}

export interface ApiPortfolioSummary {
  projectCount: number
  contractTotal: number
  received: number
  outstanding: number
  expenses: number
  servicesToDate: number
  gp: number
  gpPct: number
  closedProjectsYtd: number
  closedRevenueYtd: number
  closedProfitYtd: number
  openJobsRevenueYtd: number
  openJobsExpensesYtd: number
}

export interface ApiDashboardData {
  overdueInvoices: { id: string; label: string; amount: number; dueDate: string | null; daysOverdue: number; project: { id: string; code: string; name: string } }[]
  recentChangeOrders: { id: string; number: number; description: string; costImpact: number; status: string; createdAt: string; project: { id: string; code: string; name: string } }[]
  topVendors: { vendorId: string | null; vendor: string; totalSpend: number; poCount: number }[]
  poStatusSummary: { status: string; count: number; totalAmount: number }[]
  totalCostSavings: number
}

export interface ApiStreamGroup {
  stream: string
  count: number
  contractTotal: number
}

export interface ApiPmGroup {
  pmId: string | null
  pm: string
  count: number
  contractTotal: number
}

export interface ApiMarginRow {
  projectId: string
  costTotal: number
  sellTotal: number
  gpDollar: number
  gpPct: number
}

export interface ApiApAgingItem {
  id: string
  poNumber: string
  vendor: string
  projectId: string
  projectCode: string
  projectName: string
  amount: number
  status: string
  orderedDate: string | null
  daysOutstanding: number
  bucket: '0-30' | '31-60' | '61-90' | '90+'
}

export interface ApiApAging {
  summary: {
    current: { count: number; total: number }
    thirty:  { count: number; total: number }
    sixty:   { count: number; total: number }
    ninety:  { count: number; total: number }
    totalOutstanding: number
    poCount: number
  }
  items: ApiApAgingItem[]
}

// ─── Mutation request bodies ───────────────────────────────────────────────────

export interface ApiCreateProjectBody {
  code: string
  name: string
  accountId: string
  revenueStream: string
  location?: string
  pmId?: string
  leadEngineerId?: string
  contractTotal?: number
  targetCompletion?: string
  opportunityId?: string
  notes?: string
  jetbuiltId?: string
  color?: string
}

export interface ApiUpdateProjectBody {
  name?: string
  status?: string
  phase?: string
  pmId?: string
  leadEngineerId?: string
  contractTotal?: number
  targetCompletion?: string
  notes?: string
  color?: string | null
}

// JetBuilt-import path body
export interface ApiCreateChangeOrderJbBody {
  jetbuiltOptionId: number
}

// Native (manual) creation body
export interface ApiCreateChangeOrderManualBody {
  description: string
  scopeNotes?: string
  costImpact?: number
  scheduleImpact?: number
  status?: 'PENDING_APPROVAL' | 'APPROVED'
  clientApprovedAt?: string
}

export type ApiCreateChangeOrderBody = ApiCreateChangeOrderJbBody | ApiCreateChangeOrderManualBody

export interface ApiUpdateChangeOrderBody {
  status?: string
  description?: string
  scopeNotes?: string
  costImpact?: number
  scheduleImpact?: number
  clientApprovedAt?: string | null
}

export interface ApiChangeOrderDetail {
  id: string
  projectId: string
  number: number
  description: string
  scopeNotes: string | null
  costImpact: number
  scheduleImpact: number
  status: string
  source: 'JETBUILT' | 'MANUAL'
  clientApprovedAt: string | null
  createdAt: string
}

export interface ApiConvertCreditToCoBody {
  description: string
  scopeNotes?: string
  scheduleImpact?: number
  clientApprovedAt?: string
}

// PO line body. Single-link callers use `bomLineItemId` (sugar that the server
// normalizes to bomLinks: [{ id, qty=line.qty }]). Multi-link callers — e.g.
// 'Assign to PO' against a multi-select aggregating identical products — use
// `bomLinks` to specify split quantities. Provide one or the other, not both.
export interface ApiPoLineBody {
  bomLineItemId?: string
  bomLinks?: Array<{ bomLineItemId: string; quantity: number }>
  description: string
  quantity: number
  unitCost: number
}

export interface ApiShipToAddress {
  shipToName: string | null
  shipToStreet: string | null
  shipToCity: string | null
  shipToState: string | null
  shipToZip: string | null
}

// Shared structured ship-to / payment / shipping fields carried on create + update.
interface ApiPoShipFields {
  quoteNumber?: string | null
  paymentMethod?: string | null
  paymentMethodDetail?: string | null
  shipToAttn?: string | null
  shipToName?: string | null
  shipToStreet?: string | null
  shipToCity?: string | null
  shipToState?: string | null
  shipToZip?: string | null
  shippingMethod?: string | null
}

export interface ApiCreatePoBody extends ApiPoShipFields {
  projectId: string
  vendorId?: string
  // Omit to let the server allocate the next number in `series` (default 100). API callers
  // may still supply poNumber directly — its series is parsed from the number itself.
  poNumber?: string
  series?: 100 | 200 | 300 | 400 | 500
  isDirectPurchase?: boolean
  expectedDate?: string
  orderedDate?: string
  notes?: string
  // F3: required (server-checked) when series = 500 — the stock warehouse the gear comes
  // off the shelf at vs. the warehouse the job is staged at.
  fulfillFrom?: 'OKC' | 'ATL'
  deliverTo?: 'OKC' | 'ATL'
  lines: ApiPoLineBody[]
}

export interface ApiUpdatePoBody extends ApiPoShipFields {
  status?: string
  vendorId?: string | null
  poNumber?: string
  isDirectPurchase?: boolean
  orderedDate?: string | null
  expectedDate?: string | null
  notes?: string | null
  paidStatus?: 'UNPAID' | 'PARTIAL' | 'PAID'
  prepayAmount?: number | null
  lines?: Array<{ id?: string } & ApiPoLineBody>
}

export interface ApiRecordPoPaymentBody {
  amount: number
  receivedAt: string
  bankReference?: string
  notes?: string
}

export interface ApiReceivePoBody {
  location: string
  binReference?: string
  notes?: string
  lines: { poLineId: string; quantityReceived: number; notes?: string }[]
}

export interface ApiCreateVendorBody {
  name: string
  contactName?: string
  email?: string
  phone?: string
  paymentTerms?: string
  notes?: string
}

// PATCH /procurement/vendors/:id — null clears a field, absent leaves it unchanged.
export interface ApiUpdateVendorBody {
  name?: string
  contactName?: string | null
  email?: string | null
  phone?: string | null
  paymentTerms?: string | null
  notes?: string | null
  active?: boolean
  is1099?: boolean
  taxId?: string | null
  businessType?: string | null
}

export interface ApiUpdateInvoiceBody {
  status?: 'DRAFT' | 'ISSUED' | 'VOIDED'
  notes?: string
  label?: string
  amount?: number
  invoiceDate?: string  // ISO datetime; dueDate is recomputed server-side from invoiceDate + terms
  terms?: string
}

export interface ApiAddBomLineBody {
  section: string
  room?: string
  system?: string
  description: string
  manufacturer?: string
  model?: string
  quantity?: number
  unitCost?: number
  unitPrice?: number
  notes?: string
  sortOrder?: number
  procurementStatus?: string
  // F2: required (and ONLY meaningful) alongside procurementStatus: 'RECEIVED' — a manual
  // receipt without the warehouse. Omitted/false 409s as MANUAL_RECEIPT_CONFIRM_REQUIRED;
  // the confirm dialog that sets this to true lives in ProjectDetailView.vue.
  confirmManualReceipt?: boolean
  // Receipt status is warehouse-owned (decision #1) — never written through this body.
  // Removed deliberately so vue-tsc catches any remaining writer; see BomLineWarehouseModal.
  paidStatus?: 'UNPAID' | 'PARTIAL' | 'PAID'
  inVectorworks?: boolean
  vwInclude?: boolean
  isBudget?: boolean
  // O5: kind selector — only applies (and is only shown in the UI) when isBudget is true.
  budgetKind?: 'MARGIN' | 'REFUNDABLE_NTE'
  budgetLineItemId?: string | null
  changeOrderId?: string | null
  // Required server-side post-approval iff this mutation moves money
  // (quantity/unitCost/unitPrice). Pre-approval is a no-op.
  changeRequestId?: string | null
  funding?: ApiBomLineFunding
  _source?: 'MANUAL' | 'OD_SHEET' | 'JETBUILT' | 'VW_BRIDGE'
  // Opt-in: when this edit changes unitCost on a line linked to one or more PO lines, also
  // push the new cost onto the PO line(s) (and any sibling BOM lines sharing that PO line)
  // instead of letting the PO silently drift. Requires PROCUREMENT/EXECUTIVE.
  propagateToPo?: boolean
}

// Post-approval funding picker payload. GP = reduce gross profit (default; no side effects).
// BUDGET = make this line a child of the named budget bucket.
export interface ApiBomLineFunding {
  kind: 'GP' | 'BUDGET'
  budgetLineItemId?: string
}

export interface ApiAuditEntry {
  id: string
  action: string
  entityType: string
  entityId: string
  source: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  createdAt: string
  userId: string | null
  user: { id: string; name: string | null; email: string } | null
  entityContext: {
    id: string
    description: string | null
    manufacturer: string | null
    model: string | null
    room: string | null
    system: string | null
    number?: number
  } | null
}

export interface ApiBomAuditEntry {
  id: string
  userId: string | null
  user: { id: string; name: string; email: string } | null
  action: string
  entityType: string
  entityId: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  createdAt: string
}

export interface ApiSavingsEvent {
  id: string
  projectId: string
  category: string  // PREPAY_DISCOUNT | VENDOR_REGISTRATION | NEGOTIATED_PRICING | BOM_FUNDING | OTHER
  bufferType: string
  amount: number  // negative = draw against savings pool (BOM_FUNDING)
  description: string
  source: string | null
  bomLineItemId: string | null  // set when category = BOM_FUNDING
  recordedAt: string
  createdAt: string
}

export interface ApiCreateSavingsBody {
  category: 'PREPAY_DISCOUNT' | 'VENDOR_REGISTRATION' | 'NEGOTIATED_PRICING' | 'OTHER'
  bufferType: 'OPERATIONAL_EFFICIENCY' | 'CONTINGENCY_RESERVE' | 'RETURNED_TO_MARGIN'
  amount: number
  description: string
  source?: string
  recordedAt?: string
}

export interface ApiCreateInvoiceBody {
  projectId: string
  changeOrderId?: string
  label: string
  amount: number
  invoiceDate?: string
  terms?: string
  notes?: string
  // CLIENT_CREDIT requires a negative amount (server-enforced). Omit/STANDARD for a normal
  // invoice or the pre-existing negative-amount credit convention.
  kind?: 'STANDARD' | 'CLIENT_CREDIT'
}

export interface ApiRecordPaymentBody {
  projectId: string
  invoiceId?: string
  amount: number
  receivedAt: string
  bankReference?: string
  notes?: string
}

// ─── Reference data types ─────────────────────────────────────────────────────

export interface ApiTogglProjectHours {
  togglProjectId: number | null
  projectName: string | null
  togglUrl: string | null
  totalHours: number
  trackedDays: number
  avgPerDay14d: number
  engineers: { name: string; hours: number }[]
}

export interface ApiJetbuiltProject {
  id: number
  name: string
  customId: string
  stage: string
  total: string
  owner: string | null
  city: string | null
  state: string | null
  updatedAt: string
}

export interface ApiJetbuiltProjectDetail extends ApiJetbuiltProject {
  clientName: string | null
}

export interface ApiJetbuiltOption {
  id: number
  name: string
  sold: boolean
  total: string
  scope: string
  itemCount: number | null
  createdAt: string
}

export interface ApiRefUser {
  id: string
  name: string
  role: string
  location: string | null
  email: string
}

export interface ApiRefAccount {
  id: string
  name: string
}

export interface ApiTrip {
  id: string
  number: number
  label: string | null
  location: string
  startDate: string | null
  endDate: string | null
  project: { id: string; code: string; name: string }
  crew: { id: string; name: string; userId: string | null }[]
}

// ─── Receiving / Material Tracking types ─────────────────────────────────────

export type LocationState =
  | 'VENDOR_TRANSIT' | 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  | 'TRANSIT_SKYLARK_TRUCK' | 'TRANSIT_FREIGHT' | 'TRANSIT_COMMERCIAL'
  | 'JOBSITE' | 'DEPLOYED'

export type TransitType = 'SKYLARK_TRUCK' | 'FREIGHT' | 'COMMERCIAL'
export type ContainerType = 'BIN' | 'PALLET' | 'RACK'
export type DiscrepancyType = 'DAMAGED' | 'WRONG_ITEM' | 'QUANTITY_MISMATCH' | 'OTHER'
export type DiscrepancyStatus = 'OPEN' | 'RESOLVED' | 'RMA_INITIATED'

export interface ApiTrackedItem {
  id: string
  skylarkId: string
  encodedId: string
  description: string
  manufacturer: string | null
  model: string | null
  serialNumber: string | null
  warrantyDate: string | null
  metadata: Record<string, unknown> | null
  // Non-null marks this row a bulk/quantity entry (F5) — a bin of one make/model, its own
  // quantity — rather than a uniquely-labeled physical unit (null). Same column GET
  // /stock/items exposes as `bulkQuantity`/`quantity`.
  quantity: number | null
  location: LocationState
  deployNotes: string | null
  projectId: string | null
  bomLineItemId: string | null
  containerId: string | null
  shelfId?: string | null
  shelf?: { id: string; code: string } | null
  unitCost: number | null
  notes: string | null
  deletedAt: string | null
  deletedReason: string | null
  deletedById: string | null
  poLineId?: string | null
  createdAt: string
  updatedAt: string
  project?: { id: string; code: string; name: string } | null
  container?: { id: string; containerId: string; type: ContainerType } | null
  // M3 #20: set on check-in / PO receive when the item can't be labeled at the warehouse;
  // its label prints later via the project's site label pack (labels not printed at check-in).
  labelDeferred?: boolean
}

// Job history (M1-2 API / M1-8 UI): every project a TrackedItem has been assigned to, in
// order — `assignments` on GET /items/lookup/:id. See TrackedItemAssignment in schema.prisma
// for the write-time invariants (opened by every assign path, closed by release/re-issue).
export interface ApiTrackedItemAssignment {
  id: string
  projectId: string
  project: { id: string; code: string; name: string }
  poLineId: string | null
  poLine?: { id: string; po: { id: string; poNumber: string } } | null
  bomLineItemId: string | null
  source: 'RECEIVED' | 'OFE' | 'FROM_STOCK' | 'FROM_JOB' | 'MANUAL'
  startedAt: string
  endedAt: string | null
  endReason: 'BACK_TO_STOCK' | 'MOVED_TO_JOB' | 'UNDONE' | null
}

export interface ApiTrackedItemDetail extends ApiTrackedItem {
  poLine?: {
    id: string
    po: { id: string; poNumber: string }
  } | null
  movementEvents: ApiMovementEvent[]
  shelf?: { id: string; code: string; section: string; position: number } | null
  assignments: ApiTrackedItemAssignment[]
}

export interface ApiContainerNested {
  id: string
  containerId: string
  encodedId: string
  name: string | null
  type: ContainerType
  location: LocationState
  retired: boolean
  _count?: { items: number }
}

export interface ApiContainer {
  id: string
  containerId: string
  encodedId: string
  type: ContainerType
  name: string | null
  projectId: string | null
  description: string | null
  location: LocationState
  retired: boolean
  createdAt: string
  updatedAt: string
  project?: { id: string; code: string; name: string; color?: ProjectColor | null } | null
  parentContainer?: { id: string; containerId: string; name: string | null; type: ContainerType } | null
  _count?: { items: number; children?: number }
}

export interface ApiContainerDetail extends ApiContainer {
  items: ApiTrackedItem[]
  movementEvents: ApiMovementEvent[]
  children?: ApiContainerNested[]
  parentContainerId?: string | null
  parentContainer?: { id: string; containerId: string; name: string | null; type: ContainerType; location: LocationState } | null
}

export interface ApiMovementEvent {
  id: string
  trackedItemId: string | null
  containerId: string | null
  fromLocation: LocationState
  toLocation: LocationState
  transitType: TransitType | null
  carrierTracking: string | null
  shipmentId: string | null
  movedById: string | null
  notes: string | null
  createdAt: string
  trackedItem?: { id: string; skylarkId: string; description: string } | null
  container?: { id: string; containerId: string; type: ContainerType } | null
}

export interface ApiVendorShipment {
  id: string
  poId: string
  // Null until procurement has the info — shipments can be logged before the
  // vendor sends carrier/tracking (see shipmentLabel in lib/carrier-tracking).
  carrierName: string | null
  trackingNumber: string | null
  expectedShipDate: string | null
  expectedDelivery: string | null
  status: string
  notes: string | null
  sourceEmailId: string | null
  // Loose ref to a stashed (not yet extracted) packing-list PDF, when the
  // shipment came in off an email with an attachment.
  emailAttachmentId: string | null
  createdAt: string
  po?: { id: string; poNumber: string; project: { id: string; code: string; name: string } }
}

// A shipment line as returned by the shipment-centric endpoints (create/get/
// PATCH /vendor-shipments) — includes the PO line it's against.
export interface ApiVendorShipmentLine {
  id: string
  shipmentId: string
  poLineId: string
  quantity: number
  createdAt: string
  poLine: { id: string; lineNumber: number; description: string; quantity: number; receivedQty: number }
}

export interface ApiVendorShipmentDetail extends ApiVendorShipment {
  lines: ApiVendorShipmentLine[]
}

// A shipment line as returned by the arrivals aggregate — includes the
// parent shipment's carrier/tracking rather than the PO line's shipment ref,
// since the arrivals view is grouped by date, not by PO.
export interface ApiArrivalShipment extends ApiVendorShipment {
  po: { id: string; poNumber: string; project: { id: string; code: string; name: string } }
  lines: (Omit<ApiVendorShipmentLine, 'poLine'> & {
    poLine: { id: string; lineNumber: number; description: string; quantity: number; receivedQty: number }
  })[]
}

export interface ApiVendorShipmentArrivals {
  window: 'today' | 'week'
  rangeStart: string
  rangeEnd: string
  /** Past due, not delivered, still missing units — outside the window by definition. */
  overdue: ApiArrivalShipment[]
  days: { date: string; shipments: ApiArrivalShipment[] }[]
}

export interface ApiVendorShipmentWithPo extends ApiVendorShipment {
  po: {
    id: string
    poNumber: string
    project: { id: string; code: string; name: string }
    vendor: { id: string; name: string } | null
    lines: (ApiPOLine & { receivingLines: { quantityReceived: number }[] })[]
  }
}

export interface ApiDiscrepancy {
  id: string
  type: DiscrepancyType
  status: DiscrepancyStatus
  quantity: number
  description: string | null
  photoUrl: string | null
  resolvedAt: string | null
  notes: string | null
  createdAt: string
  receivingRecordLine: {
    id: string
    poLine: { id: string; description: string }
    receivingRecord: {
      id: string
      receivedAt: string
      po: { id: string; poNumber: string; project: { id: string; code: string; name: string } }
    }
  }
}

// RMA (M4 G2/G3) — state machine is linear and one-way except CANCELLED, reachable from any
// non-terminal status. See routes/warehouse/rma.ts ALLOWED_TRANSITIONS for the server's copy.
export type ApiRmaStatus = 'REPORTED' | 'RMA_ISSUED' | 'SHIPPED' | 'RESOLVED' | 'CANCELLED'
export type ApiRmaResolutionType = 'REPLACEMENT' | 'REPAIR' | 'CREDIT' | 'REFUND' | 'NONE'

export interface ApiRmaItem {
  id: string
  trackedItemId: string | null
  projectId: string | null
  poLineId: string | null
  reason: string
  status: ApiRmaStatus
  resolutionType: ApiRmaResolutionType | null
  rmaNumber: string | null
  vendor: string | null
  reportedById: string | null
  reportedByName: string | null
  trackingNumber: string | null
  shippedAt: string | null
  replacementItemId: string | null
  creditAmount: number | null
  resolvedAt: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  project?: { id: string; code: string; name: string } | null
  replacementItem?: { id: string; skylarkId: string; description: string } | null
  trackedItem?: {
    id: string
    skylarkId: string
    encodedId: string
    description: string
    manufacturer: string | null
    model: string | null
    serialNumber: string | null
    location: LocationState | string
    project?: { id: string; code: string; name: string } | null
    poLine?: {
      id: string
      po: { id: string; poNumber: string; vendor: { id: string; name: string } | null }
    } | null
    movementEvents: ApiMovementEvent[]
  } | null
}

export interface ApiReceivingPO extends ApiPO {
  vendorShipments: ApiVendorShipment[]
}

export interface ApiReceivingPoDetail extends ApiPO {
  lines: (ApiPOLine & {
    receivingLines: { id: string; quantityReceived: number }[]
    bomLineItem: { id: string; description: string; manufacturer: string | null; model: string | null } | null
  })[]
  receivingRecords: {
    id: string
    location: string
    receivedAt: string
    lines: { id: string; poLineId: string; quantityReceived: number; notes: string | null }[]
  }[]
  vendorShipments: ApiVendorShipment[]
}

export interface ApiReceiveResult {
  record: { id: string; lines: { id: string; poLineId: string; quantityReceived: number }[] }
  trackedItems: ApiTrackedItem[]
  newPoStatus: string
}

// R8 — GET /pos/:id/items: every tracked item ever received against a PO, across every
// receiving pass, for the "Print all labels for this PO" button (Warehouse Mode Check-in).
export interface ApiPoItemsForLabels {
  poId: string
  poNumber: string
  // description/poLineId/shelf also back PoReceiveDrawer's read-only view for a fully
  // RECEIVED PO — the label-printing caller (Warehouse Mode Check-in) just ignores them.
  items: {
    id: string
    skylarkId: string
    labelDeferred: boolean
    description: string
    poLineId: string | null
    shelf: { id: string; code: string } | null
  }[]
  printableIds: string[]
  printableCount: number
  deferredCount: number
}

// M3 #20 — deferred site labels (GET /project/:id/site-label-pack, preview only).
export interface ApiSiteLabelPackPreview {
  projectCode: string
  pendingCount: number
  totalDeferred: number
}

// LabelData shape used by useLabelPrinter.ts's enrichLabels/buildPdfHtml.
export interface ApiSiteLabelPackResult {
  header: string | null
  /** The header as a box-label-size ZPL label (3"x2", dpi-aware) — for the Zebra path. */
  headerZpl?: string | null
  count: number
  labels: {
    skylarkId: string
    encodedId: string
    description: string
    projectCode: string | null
    projectName: string | null
    projectColor?: string | null
    projectColorLabel?: string | null
    projectColorHex?: string | null
    projectColorInk?: string | null
    location: string | null
    room: string | null
    system: string | null
    manufacturer: string | null
    model: string | null
    warrantyDate: string | null
    poNumber: string | null
    itemNumber: number | null
    totalQuantity: number | null
    receivedAt: string | null
    receivedBy: string | null
    zpl: string
    isOfe?: boolean
    customerProvidedSerial?: string | null
    ofeReceivedFrom?: string | null
    isStock?: boolean
    stockWarehouse?: string | null
    shelfCode?: string | null
  }[]
  alreadyPrintedCount: number
}

export interface ApiProjectMaterials {
  project: { id: string; code: string; name: string }
  totalItems: number
  locationSummary: Record<string, number>
  items: ApiTrackedItem[]
  containers: ApiContainer[]
  outstandingPOs: ApiReceivingPO[]
}

// PATCH /warehouse/items/serials (M4 G1) — SerialCaptureStep's bulk-assign call.
export interface ApiSerialAssignment {
  itemId: string
  serialNumber: string
}

export interface ApiScanResult {
  type: 'item' | 'container' | 'shipment' | 'po' | 'asset'
  id: string
  skylarkId?: string
  containerId?: string
  poId?: string
  trackingNumber?: string
  poNumber?: string
  // M5 G4/G5 — company asset scan (AST- prefixed tag). The resolver returns just enough
  // for a Lookup-mode default card; Move mode fetches the full asset (getAsset) for the
  // richer facts + check-out/in actions.
  assetTag?: string
  name?: string
  active?: boolean
  location?: LocationState
  currentProjectId?: string | null
}

// ─── Stock without projects (M1-2 API, M1-5 UI) ───────────────────────────────

export interface ApiStockGroup {
  key: string
  manufacturer: string | null
  model: string | null
  description: string | null
  okc: number
  atl: number
  total: number
}

export interface ApiStockSummary {
  groups: ApiStockGroup[]
  totals: { okc: number; atl: number; total: number }
}

// R15 (docs/specs/warehouse-round3-feedback.md) — GET /stock/tree: warehouse -> shelf path
// (the Sortly folders, e.g. "STOCK / CABLE / FIELD CABLE") hierarchy, mirrors
// packages/api-gateway/src/lib/stock.ts StockTreeNode. `shelfIds` is what a client passes back
// as GET /stock's `shelfIds=` to scope the flat table to this node (empty at a "No shelf"
// node — pass `shelfId=none` instead there).
export interface ApiStockTreeNode {
  segment: string
  path: string[]
  total: number
  shelfIds: string[]
  children: ApiStockTreeNode[]
}

// BOM stock indicator (M5 #26) — GET /api/v1/projects/:id/stock-availability
export interface ApiStockAvailability {
  // manufacturer: set only when the shelf stock's own manufacturer differs from the BOM
  // line's (W5 — matching is model-first; a mismatch is surfaced, not hidden).
  [bomLineItemId: string]: { okc: number; atl: number; manufacturer?: string | null }
}

// R13 — GET /api/v1/warehouse/projects/:projectId/stock-check ("Stock Check Sheet"): the
// project's outstanding BOM lines that already match something on a warehouse shelf, plus
// the shelf(s) holding it. Same model-first match as ApiStockAvailability, so availableOkc/
// availableAtl mean exactly the same thing as that endpoint's okc/atl.
export interface ApiStockCheckShelf {
  location: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  shelfCode: string | null
  quantity: number
}

export interface ApiStockCheckLine {
  id: string
  room: string | null
  system: string | null
  manufacturer: string | null
  model: string | null
  description: string
  qtyNeeded: number
  availableOkc: number
  availableAtl: number
  shelves: ApiStockCheckShelf[]
}

export interface ApiStockCheckSheet {
  project: { id: string; code: string; name: string }
  lines: ApiStockCheckLine[]
}

// F6 (docs/specs/warehouse-m1-followups.md "Initial stock count sheet and first count") — GET
// /api/v1/warehouse/stock-count/report: the printable Count Sheet (before counting, Counted/
// Variance blank) and, after Warehouse Mode's Count flow has run, the Variance Report — same
// shape, mirrors packages/api-gateway/src/routes/warehouse/stock-count-report.ts.
export interface ApiStockCountEntry {
  id: string
  manufacturer: string | null
  model: string | null
  description: string
  importedQty: number | null
  currentQty: number
  lastCountedQty: number | null
  lastCountedAt: string | null
  variance: number | null
}

export interface ApiStockCountTotals {
  importedQty: number
  currentQty: number
  entryCount: number
}

export interface ApiStockCountShelfGroup {
  shelfId: string | null
  shelfCode: string | null
  entries: ApiStockCountEntry[]
  totals: ApiStockCountTotals
}

export interface ApiStockCountWarehouseGroup {
  location: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  shelves: ApiStockCountShelfGroup[]
  totals: ApiStockCountTotals
}

export interface ApiStockCountReport {
  warehouses: ApiStockCountWarehouseGroup[]
}

// F6 — Warehouse Mode Count mode (CountMode.vue). Distinct from the ApiStockCount* report
// types above (the printable Count Sheet / variance report, a separate admin surface) — these
// back the actual scan-first count-entry flow.
export interface ApiCountQuantityEntry {
  id: string
  skylarkId: string
  /** The bin/bin label's printed code (lib/item-label-zpl.ts prints this, not skylarkId) —
   * match a scan against either. */
  encodedId: string
  manufacturer: string | null
  model: string | null
  description: string
  currentQuantity: number
  importedQuantity: number | null
  barcode: string | null
  lastCountedAt: string | null
  lastCountedQuantity: number | null
}

export interface ApiCountUniqueItem {
  id: string
  skylarkId: string
  encodedId: string
  manufacturer: string | null
  model: string | null
  description: string
  lastCountedAt: string | null
  missingOnCount: boolean
}

export interface ApiCountShelfExpected {
  shelf: {
    id: string
    code: string
    location: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
    section: string
    position: number
    retired: boolean
  }
  quantityEntries: ApiCountQuantityEntry[]
  uniqueItems: ApiCountUniqueItem[]
}

/** "Found here" support — a scan that isn't on the current shelf's expected list, resolved
 * against every in-stock item so the caller can offer "Move here" (another shelf) vs "Add as
 * quantity entry" (unknown). */
export interface ApiCountResolveResult {
  id: string
  skylarkId: string
  manufacturer: string | null
  model: string | null
  description: string
  quantity: number | null
  location: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  shelfId: string | null
  shelfCode: string | null
}

export interface ApiCountSaveBody {
  counts: { itemId: string; quantity: number }[]
  presentItemIds: string[]
}

export interface ApiCountSaveResult {
  shelfId: string
  shelfCode: string
  countedEntries: number
  presentUniqueItems: number
  missingItemIds: string[]
}

export interface ApiCountProgressShelf {
  id: string
  code: string
  section: string
  counted: boolean
  lastCountedAt: string | null
}

export interface ApiCountProgress {
  warehouse: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  totalShelves: number
  countedShelves: number
  remainingShelves: number
  shelves: ApiCountProgressShelf[]
}

export interface ApiStockModelSuggestion {
  manufacturer: string | null
  model: string | null
  description: string | null
  source: 'stock' | 'catalog'
}

export interface ApiAddToStockBody {
  manufacturer: string
  model: string
  description?: string
  quantity: number
  warehouse: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  shelfId?: string
  // F5: 'labeled' (default) mints N per-unit TrackedItems; 'quantity' mints ONE quantity
  // entry (a bin — "no individual labels").
  mode?: 'labeled' | 'quantity'
  /** Quantity-entry mode only: the manufacturer barcode on the bin/shelf label, so a later
   * pull can match a scan of it. */
  barcode?: string
}

export interface ApiAddToStockResult {
  items: { id: string; skylarkId: string; encodedId: string; description: string }[]
}

export interface ApiAddQuantityEntryResult {
  entry: { id: string; skylarkId: string; encodedId: string; description: string; quantity: number }
}

// ─── Sortly import (M5 #25/G3) ────────────────────────────────────────────────
// Mirrors packages/api-gateway/src/lib/sortly-import.ts MappedSortlyRow and
// routes/warehouse/_sortly-import.ts SortlyPreviewRow/SortlyImportedRow.

export type ApiSortlyRowStatus = 'ok' | 'warning' | 'skip'
export type ApiSortlyRowWarning = 'UNKNOWN_FOLDER' | 'MISSING_MODEL' | 'ZERO_QUANTITY' | 'MISSING_SORTLY_ID'

export interface ApiSortlyPreviewRow {
  rowNumber: number
  sortlyId: string
  entryName: string
  /** Entry Name — the real export has no separate model column. */
  model: string | null
  manufacturer: string | null
  /** From Notes; may contain newlines. */
  description: string | null
  /** On-hand quantity; every row with quantity > 0 imports as one stock quantity entry. */
  quantity: number
  price: number | null
  barcode: string | null
  spoolLength: { raw: string; feet: number | null } | null
  jobNumber: string | null
  primaryFolder: string
  warehouse: 'OKC' | 'ATL'
  shelfPath: string
  shelfCode: string | null
  section: string | null
  position: number | null
  warnings: ApiSortlyRowWarning[]
  status: ApiSortlyRowStatus
  skipReason?: string
}

export interface ApiSortlyImportTotals {
  rows: number
  ok: number
  warning: number
  skip: number
}

export interface ApiSortlyPreviewResult {
  dryRun: true
  totals: ApiSortlyImportTotals
  rows: ApiSortlyPreviewRow[]
}

/** One committed source row — itemIds is empty for a recorded zero-quantity row (nothing
 *  physical to label). warehouse comes back as the full LocationState (e.g. WAREHOUSE_OKC). */
export interface ApiSortlyImportedRow {
  rowNumber: number
  sortlyId: string
  itemIds: string[]
  warehouse: string
  shelfCode: string | null
}

export interface ApiSortlyCommitResult {
  dryRun: false
  batchId: string
  totals: ApiSortlyImportTotals & { imported: number }
  rows: ApiSortlyImportedRow[]
}

/** GET /stock/items — the individual in-stock records behind one GET /stock group (M1-7
 * StockItemsDrawer drill-in). */
export interface ApiStockItem {
  id: string
  skylarkId: string
  encodedId: string
  description: string
  manufacturer: string | null
  model: string | null
  serialNumber: string | null
  location: string
  shelf: { id: string; code: string } | null
  container: { id: string; containerId: string; name: string | null } | null
  unitCost: number | null
  notes: string | null
  bulkQuantity: number | null
  bulkUnit: string | null
  inStockSince: string
  lastJob: { code: string; name: string } | null
  jobsCount: number
}

// ─── Re-issue by label / Pull (M1-3 API, M1-6 UI) ─────────────────────────────
// docs/specs/warehouse-session-2026-09-22.md decisions #3-#5 — see
// routes/warehouse/stock.ts and lib/stock-reissue.ts for the server side this mirrors.

export interface ApiPullTarget {
  id: string
  code: string
  name: string
  color: string | null
  location: string | null
  lineCount: number
  unitsOutstanding: number
}

export interface ApiPullContextLine {
  id: string
  description: string
  manufacturer: string | null
  model: string | null
  room: string | null
  system: string | null
  quantity: number
  received: number
  outstanding: number
  unitOfMeasure: string
  procurementStatus: string
  onShelf: { okc: number; atl: number }
}

export interface ApiStockPullPo {
  id: string
  poNumber: string
  status: string
  lineCount: number
  createdAt: string
  fulfillFrom: 'OKC' | 'ATL' | null
  deliverTo: 'OKC' | 'ATL' | null
  open: boolean
}

export interface ApiPullContext {
  project: { id: string; code: string; name: string; location: string | null; color: string | null }
  lines: ApiPullContextLine[]
  stockPullPos: ApiStockPullPo[]
}

// F3/R14: the CreatePoModal (series 500) line picker's source — the project's outstanding
// BOM lines that aren't already on another 500-series PO. procurementStatus is included so
// the modal can recognize an already-flagged IN_STOCK_OKC/ATL line (legacy manual-split
// workflow — trusted outright, no availability preview) vs. an ordinary line R14's
// auto-split now handles.
export interface ApiStockPoEligibleLine {
  id: string
  description: string
  manufacturer: string | null
  model: string | null
  quantity: number
  unitCost: number
  unitOfMeasure: string
  procurementStatus: string
}

// F3: Warehouse Mode Pull's rail once an open 500-series PO is picked/scanned.
export interface ApiPullPoLine {
  id: string
  bomLineItemId: string | null
  description: string
  manufacturer: string | null
  model: string | null
  unitOfMeasure: string
  quantity: number
  receivedQty: number
  outstanding: number
}

export interface ApiPullPoTransfer {
  id: string
  status: 'PREPARING' | 'IN_TRANSIT' | 'ARRIVED'
  transitType: string
  carrierTracking: string | null
  itemCount: number
}

export interface ApiPullPo {
  id: string
  poNumber: string
  status: string
  projectId: string
  project: { id: string; code: string; name: string }
  fulfillFrom: 'OKC' | 'ATL' | null
  deliverTo: 'OKC' | 'ATL' | null
  open: boolean
  transfer: ApiPullPoTransfer | null
  lines: ApiPullPoLine[]
}

export interface ApiReissueBody {
  code?: string
  itemId?: string
  projectId: string
  poId: string
  bomLineItemId?: string
  /** Bypasses the server's model-match check on bomLineItemId (MODEL_MISMATCH) — only send
   * this from the operator's deliberate "Choose a line" pick, never from a merely-locked
   * default line. */
  forceLineId?: boolean
  allowFromJob?: boolean
}

export interface ApiPullTransfer { id: string; status: 'PREPARING' }

export interface ApiReissueResult {
  outcome: 'OK' | 'WARN' | 'ALREADY'
  message: string
  // `serialNumber` (M4 G1) — lets the client offer "Add serial" only when the item doesn't
  // already have one.
  item: { id: string; skylarkId: string; description: string; location: string; serialNumber: string | null }
  assignmentId?: string
  po?: { id: string; poNumber: string }
  bomLine?: {
    id: string
    description: string
    manufacturer: string | null
    model: string | null
    quantity: number
    received: number
    outstanding: number
  }
  previousProject?: { id: string; code: string; name: string }
  pulledFrom?: { warehouse: 'OKC' | 'ATL' | null; shelfCode: string | null }
  /** Set when this PO's fulfillFrom != deliverTo — the item joined its one open transfer. */
  transfer?: ApiPullTransfer
}

// F5: the quantity-entry counterpart to reissue — scan a manufacturer barcode, or pick
// make/model straight off a PO line, to decrement a stock bin and mint a fresh labeled item.
export interface ApiReissueEntryBody {
  projectId: string
  poId: string
  entryId?: string
  barcode?: string
  manufacturer?: string
  model?: string
  bomLineItemId?: string
  forceLineId?: boolean
}

export interface ApiReissueEntryResult extends ApiReissueResult {
  mintedFromEntry: { id: string; skylarkId: string; remainingQuantity: number }
}

export interface ApiReissueQuantityBody {
  projectId: string
  bomLineItemId: string
  warehouse: 'OKC' | 'ATL'
  quantity: number
  poId: string
}

export interface ApiReissueQuantityResult {
  items: { id: string; skylarkId: string; shelfCode: string | null }[]
  po?: { id: string; poNumber: string }
  assignmentIds: string[]
}

export interface ApiUndoReissueResult {
  ok: true
  item: ApiTrackedItem | null
}

/** POST /stock/reissue-bulk — the bulk (non-EACH) counterpart to reissue-quantity: cable,
 * spools and other bulk IN_STOCK_OKC/ATL BOM lines have no per-item label to scan, so this
 * receipts a quantity (in the line's own unit of measure) onto the open 500 PO's line and
 * mints `physicalUnits` bulk labels. */
export interface ApiReissueBulkBody {
  projectId: string
  bomLineItemId: string
  warehouse: 'OKC' | 'ATL'
  quantityReceived: number
  physicalUnits?: number
  poId: string
}

export interface ApiReissueBulkResult {
  message: string
  items: { id: string; skylarkId: string; encodedId: string }[]
  po: { id: string; poNumber: string }
  bomLine: {
    id: string
    description: string
    quantity: number
    received: number
    outstanding: number
    unitOfMeasure: string
  }
  transfer?: ApiPullTransfer
}

/** POST /stock/release (M1-8 UI, item page "Return to stock") — puts a job item back into
 * warehouse stock at its current location, keeping its ID/label. */
export interface ApiReleaseToStockBody {
  itemId: string
  shelfId?: string
}

// ─── Stock CRUD (M5 W4) ───────────────────────────────────────────────────────

export interface ApiUpdateStockItemBody {
  manufacturer?: string | null
  model?: string | null
  description?: string
  unitCost?: number | null
  notes?: string | null
  quantity?: number
  warehouse?: 'WAREHOUSE_OKC' | 'WAREHOUSE_ATL'
  shelfId?: string | null
  containerId?: string | null
}

export interface ApiMoveStockItemsBody {
  itemIds: string[]
  shelfId?: string | null
  containerId?: string | null
}

export interface ApiMoveStockItemsResult {
  moved: number
  shelfCode: string | null
  containerCode: string | null
}

// ─── Return sessions (M2 G1/G3, docs/specs/warehouse-m2-returns.md) ──────────
// A return session is one trip back from one job: OPEN while scanning, CLOSED once
// everything's been pushed back into stock, then PENDING/REVIEWED for the (separate, G2)
// credit review workflow — see routes/warehouse/returns.ts for the server side of this shape.

export interface ApiReturnSession {
  id: string
  reference: string
  status: 'OPEN' | 'CLOSED'
  location: 'OKC' | 'ATL'
  notes: string | null
  openedById: string | null
  closedAt: string | null
  closedById: string | null
  reviewStatus: 'PENDING' | 'REVIEWED' | null
  reviewedAt: string | null
  reviewedById: string | null
  creditMemoId: string | null
  /** Set once review creates a memo (any CREDIT line) — R6, docs/specs/warehouse-round2-feedback.md. */
  creditMemo?: { id: string; reference: string; status: string } | null
  sourceProjectId: string | null
  sourceProject?: { id: string; code: string; name: string } | null
  returnedAt: string
  createdAt: string
  _count?: { lines: number }
}

export interface ApiReturnLine {
  id: string
  returnRecordId: string
  trackedItemId: string | null
  trackedItem: {
    id: string
    skylarkId: string
    description: string
    manufacturer: string | null
    model: string | null
    location: string
    serialNumber: string | null
  } | null
  bomLineItemId: string | null
  bomLineItem: {
    id: string
    description: string
    manufacturer: string | null
    model: string | null
    unitPrice: number
    unitOfMeasure: string
    room: string | null
    system: string | null
  } | null
  manufacturer: string | null
  model: string | null
  description: string | null
  quantity: number
  sourceProjectId: string | null
  condition: 'GOOD' | 'DAMAGED' | 'UNKNOWN'
  disposition: 'RESHELVE' | 'REDEPLOY' | 'RMA' | 'SCRAP'
  creditDecision: 'CREDIT' | 'KEEP' | null
  creditQuantity: number | null
  creditUnitPrice: number | null
  notes: string | null
}

export interface ApiReturnSessionDetail extends ApiReturnSession {
  lines: ApiReturnLine[]
}

/** One return session awaiting review — GET /projects/:id/returns/pending (W11 BOM alert). */
export interface ApiPendingReturn {
  id: string
  reference: string
  closedAt: string | null
  lineCount: number
}

/** POST /returns/sessions/:id/review body, one per line (W11). quantity/unitPrice are
 * optional for CREDIT — the server defaults quantity to the line's own quantity and
 * unitPrice to the BOM line's sale price when omitted (lib/return-review.ts). */
export interface ApiReviewDecisionBody {
  lineId: string
  decision: 'CREDIT' | 'KEEP'
  quantity?: number
  unitPrice?: number
}

export interface ApiOpenReturnSessionBody {
  projectId: string
  location: 'OKC' | 'ATL'
  notes?: string
}

/** One ReturnLine added by a scan — for an item scan this is the whole result; for a
 * container scan, one of these lands per item found inside it (routes/warehouse/
 * _return-session.ts `AddedReturnLine`). */
export interface ApiAddedReturnLine {
  lineId: string
  itemId: string
  quantity: number
  needsQuantity: boolean
  warning?: 'OTHER_JOB'
  otherJob?: { id: string; code: string; name: string }
}

export type ApiReturnScanResult =
  | ({ type: 'item' } & ApiAddedReturnLine)
  | {
      type: 'container'
      lines: ApiAddedReturnLine[]
      skipped: { itemId: string; code: string; message: string }[]
    }

export interface ApiAddReturnLineBody {
  bomLineItemId?: string
  manufacturer?: string
  model?: string
  description?: string
  quantity?: number
}

export interface ApiUpdateReturnLineBody {
  quantity?: number
  condition?: 'GOOD' | 'DAMAGED' | 'UNKNOWN'
  notes?: string
}

// Request bodies

export interface ApiReceivePoItemsBody {
  location: LocationState
  // R7: default shelf for the whole receive — applies to every line unless a line sets
  // its own `shelfId` below. Required (own value or this default) for every line when
  // `location` is a warehouse (WAREHOUSE_OKC/ATL); ignored for JOBSITE.
  shelfId?: string
  lines: {
    poLineId: string
    quantityReceived: number
    physicalUnits?: number
    notes?: string
    containerType?: 'RACK' | 'BIN' | 'PALLET'
    // M3 #20 — "Can't label here — send labels to site": items from this line get
    // labelDeferred=true and are never printed at check-in.
    labelDeferred?: boolean
    // R7: per-line shelf override — falls back to the receive's default `shelfId` above.
    shelfId?: string
  }[]
  notes?: string
}

export interface ApiUpdateItemBody {
  serialNumber?: string
  warrantyDate?: string
  metadata?: Record<string, unknown>
  deployNotes?: string
  projectId?: string | null
  // OFE-only patches: server merges these into TrackedItem.metadata. Pass null to clear.
  customerProvidedSerial?: string | null
  ofeNotes?: string | null
  // Timothy #1: shelf location on any item (not just job-less stock) — null clears it.
  // Validated server-side against the item's current warehouse (400 SHELF_WRONG_WAREHOUSE).
  shelfId?: string | null
  // Timothy #2: free-text note on any individual item, e.g. "out of box". Null clears it.
  notes?: string | null
}

// ─── OFE (Owner Furnished Equipment) ──────────────────────────────────────────

export type OfeReceivedFrom = 'drop-off' | 'warehouse-pickup' | 'jobsite-direct'

export interface ApiOfeMetadata {
  ofeSource: 'customer'
  customerProvidedSerial?: string
  ofeReceivedFrom?: OfeReceivedFrom
  ofeNotes?: string
}

export interface ApiOfeBomLine {
  id: string
  description: string
  manufacturer: string | null
  model: string | null
  unitOfMeasure: string
  quantity: number
  room: string | null
  system: string | null
  receivingStatus: 'NOT_RECEIVED' | 'PARTIAL' | 'RECEIVED'
  receivedQty: number
  outstanding: number
}

export interface ApiReceiveOfeBody {
  projectId: string
  location: LocationState
  receivedFrom: OfeReceivedFrom
  lines: {
    bomLineItemId: string
    quantityReceived: number
    physicalUnits?: number
    customerProvidedSerial?: string
    notes?: string
  }[]
  notes?: string
}

export interface ApiReceiveOfeResult {
  record: { id: string; lines: { id: string; bomLineItemId: string | null; quantityReceived: number }[] }
  trackedItems: { id: string; skylarkId: string; encodedId: string; description: string }[]
}

export interface ApiCreateItemBody {
  description: string
  manufacturer?: string
  model?: string
  serialNumber?: string
  warrantyDate?: string
  metadata?: Record<string, unknown>
  projectId?: string | null
  location: LocationState
}

export interface ApiCreateContainerBody {
  type: ContainerType
  projectId?: string
  description?: string
  location: LocationState
}

export interface ApiUpdateContainerBody {
  projectId?: string | null
  description?: string
  name?: string
  retired?: boolean
}

export interface ApiShipBody {
  itemIds?: string[]
  containerIds?: string[]
  transitType: TransitType
  carrierTracking?: string
  destination: LocationState
  notes?: string
}

export interface ApiArriveBody {
  itemIds?: string[]
  containerIds?: string[]
  location: LocationState
  notes?: string
}

// ─── Outbound Shipments ───────────────────────────────────────────────────────

export type OutboundShipmentStatus = 'PREPARING' | 'IN_TRANSIT' | 'ARRIVED'

export interface ApiOutboundShipmentItem {
  id: string
  skylarkId: string
  description: string
  location: LocationState
  projectId: string | null
  project: { id: string; code: string; name: string } | null
}

export interface ApiOutboundShipmentContainer {
  id: string
  containerId: string
  name: string | null
  type: string
  location: LocationState
  _count: { items: number }
  /** The container's contents (Check-in → Transfer verifies them under the container). */
  items?: { id: string; skylarkId: string; description: string; location: LocationState }[]
  /** Containers nested inside this one (pallet → bin → items), recursively. */
  children?: ApiOutboundShipmentContainer[]
}

export interface ApiOutboundShipment {
  id: string
  status: OutboundShipmentStatus
  transitType: TransitType
  destination: LocationState
  carrierTracking: string | null
  notes: string | null
  createdById: string | null
  shippedAt: string | null
  arrivedAt: string | null
  // R12: destination address / expected arrival for a shipment headed to a job site or the
  // other warehouse. Both nullable/editable (see updateOutboundShipment above).
  destinationAddress: string | null
  expectedArrival: string | null
  createdAt: string
  updatedAt: string
  items: ApiOutboundShipmentItem[]
  containers: ApiOutboundShipmentContainer[]
  _count?: { items: number; containers: number }
}

export interface ApiCreateOutboundShipmentBody {
  transitType: TransitType
  destination: LocationState
  carrierTracking?: string
  notes?: string
  // R12: destinationAddress is stored as given; when destination is JOBSITE and none is
  // given, projectId (a hint only — not persisted) defaults it from the project's account
  // address server-side. Always editable afterward.
  destinationAddress?: string
  expectedArrival?: string
  projectId?: string
}

export interface ApiCreateVendorShipmentBody {
  poId: string
  carrierName?: string
  trackingNumber?: string
  expectedShipDate?: string
  expectedDelivery?: string
  notes?: string
  lines?: { poLineId: string; quantity: number }[]
}

// PATCH /vendor-shipments/:id — null (or blank text) clears a field, absent
// leaves it unchanged.
export interface ApiUpdateVendorShipmentBody {
  status?: 'IN_TRANSIT' | 'DELIVERED' | 'DELAYED'
  carrierName?: string | null
  trackingNumber?: string | null
  expectedShipDate?: string | null
  expectedDelivery?: string | null
  notes?: string
  lines?: { poLineId: string; quantity: number }[]
}

export interface ApiCreateDiscrepancyBody {
  receivingRecordLineId: string
  type: DiscrepancyType
  quantity?: number
  description?: string
  photoUrl?: string
  notes?: string
}

export interface ApiUpdateDiscrepancyBody {
  status?: DiscrepancyStatus
  notes?: string
  rmaItemId?: string
}

// ─── Pair Mode types ──────────────────────────────────────────────────────────

export interface ApiPairSession {
  sessionId: string
  code: string
  expiresAt: string
}

export interface ApiPairJoinResult {
  sessionId: string
  deviceId: string
  currentMode?: string
  currentCheckinPoId?: string | null
}

export interface ApiPairState {
  currentMode: string
  currentCheckinPoId: string | null
}

export interface ApiPairDevice {
  deviceId: string
  deviceName: string
  joinedAt: string
}

export interface ApiPairSessionStatus {
  sessionId: string
  code: string
  devices: ApiPairDevice[]
  createdAt: string
  expiresAt: string
}

export interface ApiPairScanEvent {
  deviceId: string
  deviceName: string
  scannedCode: string
  scanResult: any
  timestamp: string
}

// ─── Document generation types ────────────────────────────────────────────────

export interface ApiDocumentPO {
  id: string
  poNumber: string
  status: string
  quoteNumber: string | null
  totalAmount: number
  expectedDate: string | null
  orderedDate: string | null
  notes: string | null
  paymentMethod: string | null
  paymentMethodDetail: string | null
  shipToAttn: string | null
  shipToName: string | null
  shipToStreet: string | null
  shipToCity: string | null
  shipToState: string | null
  shipToZip: string | null
  shippingMethod: string | null
  vendor: {
    id: string
    name: string
    contactName: string | null
    email: string | null
    phone: string | null
    paymentTerms: string | null
  } | null
  project: {
    id: string; code: string; name: string; location: string
    account: { name: string; address: string | null; phone: string | null } | null
  }
  lines: {
    id: string; description: string; quantity: number; unitCost: number; totalCost: number
    bomLineItem: { manufacturer: string | null; model: string | null } | null
  }[]
}

export interface ApiDocumentInvoice {
  id: string
  label: string
  amount: number
  dueDate: string | null
  issuedAt: string | null
  status: string
  terms: string | null
  notes: string | null
  project: {
    id: string; code: string; name: string; contractTotal: number
    account: { name: string; address: string | null; phone: string | null } | null
  }
  payments: { amount: number; receivedAt: string | null; bankReference: string | null }[]
  changeOrder: { id: string; number: number; description: string | null } | null
}

export interface ApiDocumentContainerItem {
  id: string
  skylarkId: string
  encodedId: string
  description: string
  manufacturer: string | null
  model: string | null
  serialNumber: string | null
  location: string
  rackContainer: {
    id: string
    containerId: string
    type: string
    description: string | null
    items: {
      id: string; skylarkId: string; encodedId: string; description: string
      manufacturer: string | null; model: string | null; serialNumber: string | null
      location: string
    }[]
  } | null
}

export interface ApiDocumentContainer {
  id: string
  containerId: string
  type: string
  description: string | null
  location: string
  project: { id: string; code: string; name: string } | null
  items: ApiDocumentContainerItem[]
}

// ─── Google Drive types ───────────────────────────────────────────────────────

export interface ApiDriveFile {
  id: string
  name: string
  mimeType: string
  size?: string
  modifiedTime?: string
  webViewLink?: string
  iconLink?: string
  thumbnailLink?: string
  parents?: string[]
  description?: string
}

export interface ApiDriveFileList {
  files: ApiDriveFile[]
  nextPageToken: string | null
}

export interface ApiDrivePoFile extends ApiDriveFile {
  parsedPoNumber: string | null
  parsedVendor: string | null
  isPdf: boolean
  isXlsx: boolean
}

export interface ApiOdCompareRow {
  odRow: {
    room: string | null; system: string | null
    sectionType: 'BASE_BOM' | 'CHANGE_ORDER' | 'INSTALL_SERVICES' | 'MATERIALS' | 'CONTINGENCY'
    poNumber: string; procStatus: string
    qty: number; mfgCode: string; model: string; description: string
    unitCost: number; extCost: number; gpPct: number; unitPrice: number; extension: number
    notes: string
  } | null
  bomItem: {
    id: string; description: string; manufacturer: string | null; model: string | null
    quantity: number; unitCost: number; totalCost: number; unitPrice: number; totalPrice: number
    gpPercent: number; room: string | null; system: string | null; section: string
    deletedAt: string | null; isBudget: boolean
  } | null
  match: 'matched' | 'od_only' | 'northstar_only'
  deltas: { field: string; od: string | number; ns: string | number }[]
}

export interface ApiOdCompareResult {
  sheetId: string
  sheetTitle: string
  odRowCount: number
  bomItemCount: number
  summary: {
    matched: number
    withDiscrepancies: number
    odOnly: number
    northstarOnly: number
    odTotal: number       // base contract only (excludes change orders)
    odCoTotal: number     // change order sections total
    northstarTotal: number
    delta: number
    sectionBreakdown: { type: 'BASE_BOM' | 'CHANGE_ORDER' | 'INSTALL_SERVICES' | 'CONTINGENCY'; rowCount: number; total: number }[]
  }
  rows: ApiOdCompareRow[]
}

export interface ApiAccountingAssertion {
  name: string
  status: 'PASS' | 'FAIL' | 'WARNING' | 'INFO'
  expected?: number
  actual?: number
  delta?: number
  message: string
}

export interface ApiAccountingCheck {
  projectId: string
  projectCode: string
  projectName: string
  status: 'PASS' | 'FAIL' | 'WARNING'
  assertions: ApiAccountingAssertion[]
  summary: {
    contractTotal: number
    bomBaseTotal: number
    bomCoTotal: number
    baseInvoicesTotal: number
    coInvoicesTotal: number
    creditInvoicesTotal: number
    creditMemoTotal: number
    approvedCoTotal: number
    netBilled: number
    totalPaid: number
    balance: number
    approvedCos: number
    issuedCreditMemos: number
  }
}

export interface ApiDriveImportResult {
  poId: string
  poNumber: string
  vendor: string | null
  lineCount: number
  totalAmount: number
  bomLinkedCount: number
  unmatchedPoLines: number
}

export interface ApiPoImportPreview {
  poNumber: string
  vendorName: string
  quoteNumber: string | null
  date: string | null
  notes: string | null
  totalAmount: number
  lines: {
    index: number
    description: string
    manufacturer: string
    model: string
    quantity: number
    unitCost: number
    totalCost: number
    bomAllocations: {
      bomLineItemId: string
      allocatedQty: number
      bomDescription: string
      bomManufacturer: string | null
      bomModel: string | null
      bomQuantity: number
      previouslyOrderedQty: number
      bomUnitCost: number
      poUnitCost: number
      priceDiffers: boolean
    }[]
    firstBomId: string | undefined
    unmatched: boolean
  }[]
  unmatchedPoLineCount: number
}

// ─── Credit Memo types ────────────────────────────────────────────────────────

export interface ApiCreditMemoLine {
  id: string
  creditMemoId: string
  bomLineItemId: string | null
  description: string
  originalAmount: number
  creditAmount: number
  quantity: number | null
  unitPrice: number | null
  room: string | null
  system: string | null
  notes: string | null
  createdAt: string
  bomLineItem: {
    id: string; description: string; manufacturer: string | null
    model: string | null; quantity: number; room: string | null; system: string | null
    unitCost: number; originalTotalCost: number | null
  } | null
}

export interface ApiCreditMemoReplacement {
  id: string
  creditMemoId: string
  bomLineItemId: string
  notes: string | null
  createdAt: string
  bomLineItem: {
    id: string; section: string; room: string | null; system: string | null
    description: string; manufacturer: string | null; model: string | null
    quantity: number; totalPrice: number; totalCost: number
    changeOrderId: string | null; deletedAt: string | null
  }
}

export interface ApiCreditMemoFunding {
  id: string
  creditMemoId: string
  // Funding reconciles to either a Change Order (customer-billable) or a Change
  // Request (internal cost-move, e.g. a bulk swap). Exactly one of these is set.
  changeOrderId: string | null
  changeOrder: { id: string; number: number; description: string; costImpact: number } | null
  changeRequestId: string | null
  changeRequest: { id: string; title: string; reason: string; status: string } | null
  amountApplied: number
  notes: string | null
  createdAt: string
}

export interface ApiCreateCreditFundingBody {
  changeOrderId?: string
  changeRequestId?: string
  amountApplied: number
  notes?: string
}

export interface ApiCreditMemo {
  id: string
  projectId: string
  reference: string
  description: string
  status: 'DRAFT' | 'ISSUED' | 'APPLIED'
  totalAmount: number
  notes: string | null
  issuedAt: string | null
  appliedAt: string | null
  createdAt: string
  updatedAt: string
  lines: ApiCreditMemoLine[]
  funding: ApiCreditMemoFunding[]
  replacements: ApiCreditMemoReplacement[]
}

// ─── Bulk swap (issue #29) ───────────────────────────────────────────────────

export type SwapSkipReason =
  | 'NOT_FOUND'
  | 'DELETED'
  | 'CREDITED'
  | 'PARTIAL_CREDIT'
  | 'IN_CHANGE_ORDER'
  | 'BUDGET_BUCKET'

export interface ApiBulkSwapPreviewBody {
  bomLineItemIds: string[]
  replacement?: { unitCost: number; unitPrice: number }
}

export interface ApiBulkSwapEligibleLine {
  id: string
  section: string
  room: string | null
  system: string | null
  description: string
  manufacturer: string | null
  model: string | null
  quantity: number
  unitPrice: number
  totalPrice: number
}

export interface ApiBulkSwapPreviewResponse {
  eligibleLines: ApiBulkSwapEligibleLine[]
  skippedLines: { lineId: string; reason: SwapSkipReason }[]
  totals: { creditTotal: number; replacementTotal: number; delta: number } | null
  vwWarning: { affected: number; total: number } | null
}

export interface ApiBulkSwapBody {
  bomLineItemIds: string[]
  replacement: {
    manufacturer: string
    model: string
    description?: string
    unitCost: number
    unitPrice: number
    vwMake?: string
    vwModel?: string
  }
  description: string
  notes?: string
  deltaFunding?: { kind: 'GP' }
  changeRequestId?: string | null
}

export interface ApiBulkSwapResponse {
  creditMemo: ApiCreditMemo
  // Bulk swap no longer auto-creates a Change Order; it tracks under the active CR.
  // Field retained on the wire for backward-compat with any caller still reading it.
  changeOrder: ApiChangeOrderDetail | null
  replacementLines: ApiBomLineItem[]
  skippedLines: { lineId: string; reason: SwapSkipReason }[]
  totals: {
    creditTotal: number
    replacementTotal: number
    delta: number
    amountApplied: number
    customerBalance: number
  }
}

export interface ApiCreateCreditMemoBody {
  description: string
  notes?: string
  amount?: number
  lines: {
    bomLineItemId?: string
    description: string
    originalAmount?: number
    creditAmount?: number
    quantity?: number
    unitPrice?: number
    notes?: string
  }[]
}

export interface ApiUpdateCreditMemoBody {
  description?: string
  notes?: string | null
  status?: 'ISSUED'
}

export interface ApiCreateCreditMemoLineBody {
  bomLineItemId?: string
  description: string
  originalAmount?: number
  creditAmount: number
  notes?: string
}

export interface ApiDocumentCreditMemo {
  id: string
  reference: string
  description: string
  status: string
  totalAmount: number
  notes: string | null
  issuedAt: string | null
  appliedAt: string | null
  createdAt: string
  project: {
    id: string; code: string; name: string
    account: { name: string; address: string | null; phone: string | null } | null
  }
  lines: {
    id: string; description: string; originalAmount: number; creditAmount: number; notes: string | null
    bomLineItem: { id: string; description: string; manufacturer: string | null; model: string | null; quantity: number; unitPrice: number } | null
  }[]
}

export interface ApiDocumentChangeOrder {
  id: string
  number: number
  description: string
  scopeNotes: string | null
  costImpact: number
  scheduleImpact: number
  status: string
  source: 'JETBUILT' | 'MANUAL'
  clientApprovedAt: string | null
  createdAt: string
  project: {
    id: string; code: string; name: string; location: string
    account: { name: string; address: string | null; phone: string | null } | null
  }
  creditFunding: Array<{
    id: string
    amountApplied: number
    notes: string | null
    creditMemo: { id: string; reference: string; totalAmount: number }
  }>
  lines: Array<{
    id: string; description: string; manufacturer: string | null; model: string | null
    room: string | null; system: string | null
    quantity: number; unitCost: number; unitPrice: number; totalPrice: number
  }>
}

// ─── PDF Intelligence ─────────────────────────────────────────────────────────

// F2 (a): one quote line (qty N) may price several BOM lines.
export interface ApiPdfExtractionLineBomLink {
  id: string
  extractionLineId: string
  bomLineItemId: string
  quantity: number
  createdAt: string
}

export interface ApiPdfExtractionLine {
  id: string
  extractionId: string
  lineNumber: number
  description: string
  manufacturer: string | null
  model: string | null
  quantity: number
  unitPrice: number | null
  totalPrice: number | null
  serialNumbers: string[]
  matchedBomLineId: string | null
  matchConfidence: number | null
  notes: string | null
  /** True once this line was consumed to create a PO line or receiving record. */
  committed: boolean
  /**
   * Set once this line's unit price has been written onto its BOM line(s) by
   * commit/prices. Distinct from `committed`: a priced line can still be
   * ordered, and a priced line is frozen — the server 409s
   * LINE_ALREADY_PRICED on any edit to it.
   */
  pricedAt: string | null
  bomLinks: ApiPdfExtractionLineBomLink[]
}

// M0-8: PACKING_LIST + COMPLETE extractions get this back from GET /pdf-intel/:id
// once their rawText's poReference matches an open PO — see lib/po-matcher.ts.
export interface ApiPackingSlipPoMatch {
  poId: string
  poNumber: string
  confidence: 'EXACT' | 'NORMALIZED' | 'SUBSTRING' | 'FUZZY'
  distance: number
  status: string
  projectCode: string
  /** The raw reference string read off the packing slip, e.g. "SKY 2737-101". */
  reference: string
}

// G4/G5: fallback PO ranking when a packing slip carries no usable PO
// reference (or nothing matched it) — see lib/po-ranking.ts. Only ever set
// when poMatch is null and no PO is attached to the extraction yet.
export interface ApiPoRankCandidate {
  poId: string
  poNumber: string
  /** matchedLines / totalSlipLines, 0..1. */
  score: number
  matchedLines: number
  totalSlipLines: number
}

export interface ApiPoRankOutcome {
  /** Set only when exactly one candidate clearly wins. */
  autoSelected: ApiPoRankCandidate | null
  candidates: ApiPoRankCandidate[]
}

export interface ApiPdfExtraction {
  id: string
  type: 'QUOTE' | 'INVOICE' | 'PACKING_LIST'
  status: 'PENDING' | 'PROCESSING' | 'COMPLETE' | 'FAILED'
  projectId: string
  sourcePoId: string | null
  filename: string
  driveFileId: string | null
  driveFiledAt: string | null
  rawText: string | null
  errorMessage: string | null
  inputTokens: number | null
  outputTokens: number | null
  modelName: string | null
  costUsd: string | null  // Prisma Decimal serializes as string
  escalated: boolean
  escalationReason: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
  lines: ApiPdfExtractionLine[]
  poMatch?: ApiPackingSlipPoMatch | null
  /** G4/G5: only computed when poMatch is null and no PO is attached yet. */
  poRanking?: ApiPoRankOutcome | null
}

// F2 (b/T4/T5): result of POST /pdf-intel/:id/commit/prices
export interface ApiPdfPriceCommitDecision {
  bomLineItemId: string
  rule: 'RULE_1_BASELINE' | 'RULE_2_CREDIT_BUDGET'
  requiresCr: boolean
  direction: 'increase' | 'decrease' | 'unchanged'
  creditMemoId?: string
}

export interface ApiPdfPriceCommitResult {
  updatedBomLineIds: string[]
  decisions: ApiPdfPriceCommitDecision[]
  changeRequestId: string | null
  driveFiling: { filed: boolean; reason?: string; fileId?: string }
  committedAt: string
  /** The quote lines this commit priced (PdfExtractionLine ids). */
  quoteLineIds: string[]
  /**
   * Cost impact of the batch on the BOM, summed server-side from the same
   * before/after figures the BOM updates and audit rows were written with —
   * so the completion summary can never disagree with what was saved.
   */
  costImpact: { beforeTotalCost: number; afterTotalCost: number; delta: number }
}

// ─── Gmail Inbox ──────────────────────────────────────────────────────────────

export type ApiEmailClass = 'SHIPMENT_NOTICE' | 'PACKING_LIST' | 'ORDER_CONFIRM' | 'BACKORDER_NOTICE' | 'INVOICE' | 'QUOTE' | 'OTHER' | 'UNKNOWN'
export type ApiEmailStatus = 'PENDING' | 'PROCESSED' | 'IGNORED' | 'FAILED'

export interface ApiGmailStatus {
  configured: boolean
  lastPollAt: string | null
  status: string
  errorCount: number
  pendingCount: number
}

export interface ApiEmailAttachment {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  gmailAttachmentId: string
  extractionId: string | null
}

export interface ApiInboundEmail {
  id: string
  gmailMessageId: string
  gmailThreadId: string
  receivedAt: string
  fromAddress: string
  fromName: string | null
  subject: string
  snippet: string | null
  bodyText: string | null
  classification: ApiEmailClass | null
  extractedPoNumber: string | null
  extractedTracking: string | null
  extractedCarrier: string | null
  extractedData: ApiExtractedEmailData | null
  matchedPoId: string | null
  matchedPo: {
    id: string
    poNumber: string
    status: string
    project: { id: string; code: string; name: string }
  } | null
  status: ApiEmailStatus
  processedAt: string | null
  processedById: string | null
  notes: string | null
  attachments: ApiEmailAttachment[]
  createdAt: string
  updatedAt: string
}

export interface ApiGmailProcessResult {
  action: 'SHIPMENT_CREATED' | 'PACKING_LIST_EXTRACTED' | 'PO_MARKED_ORDERED' | 'PO_MARKED_BACKORDERED' | 'NO_ACTION'
  shipmentId?: string
  extractionId?: string
  poId?: string
  classification?: string
}

// ─── AI Usage Reporting ──────────────────────────────────────────────────────

export interface ApiAiUsageTotals {
  calls: number
  inputTokens: number
  outputTokens: number
  totalTokens: number
  costUsd: number
}

export interface ApiAiUsageProjectRow {
  projectId: string | null
  code: string | null
  name: string | null
  inputTokens: number
  outputTokens: number
  costUsd: number
  calls: number
}

export interface ApiAiUsageMonthRow {
  month: string  // YYYY-MM
  inputTokens: number
  outputTokens: number
  costUsd: number
  calls: number
}

export interface ApiAiUsageModelRow {
  model: string
  inputTokens: number
  outputTokens: number
  costUsd: number
  calls: number
}

export interface ApiAiUsageProjectModelRow extends ApiAiUsageProjectRow {
  model: string
}

export interface ApiAiUsageUseCaseRow {
  useCase: string
  phase: string
  inputTokens: number
  outputTokens: number
  costUsd: number
  calls: number
}

export interface ApiAiUsageReport {
  from: string
  to: string
  groupBy: string
  totals: ApiAiUsageTotals
  byProject: ApiAiUsageProjectRow[]
  byMonth: ApiAiUsageMonthRow[]
  byModel: ApiAiUsageModelRow[]
  byProjectModel: ApiAiUsageProjectModelRow[]
  byUseCase: ApiAiUsageUseCaseRow[]
}

// ─── Gmail — rich extraction + suggested actions ─────────────────────────────

export interface ApiExtractedShipment {
  trackingNumber: string | null
  carrier: string | null
  shipDate: string | null
  expectedDelivery: string | null
}
export interface ApiExtractedInvoice {
  invoiceNumber: string | null
  amount: number | null
  currency: string | null
  dueDate: string | null
}
export interface ApiExtractedLineItem {
  description: string
  manufacturer: string | null
  model: string | null
  quantity: number
  unitPrice: number | null
}
export interface ApiExtractedAttachmentHint {
  filename: string
  type: 'packing_list' | 'invoice' | 'quote' | 'po_confirmation' | 'spec_sheet' | 'other'
}
export interface ApiExtractedEmailData {
  intent: string
  category: ApiEmailClass
  projectHints: string[]
  poReferences: string[]
  shipment: ApiExtractedShipment | null
  invoice: ApiExtractedInvoice | null
  lineItems: ApiExtractedLineItem[]
  attachmentHints: ApiExtractedAttachmentHint[]
  summary: string
}

export type ApiActionType =
  | 'CREATE_VENDOR_SHIPMENT'
  | 'ATTACH_PACKING_LIST'
  | 'UPDATE_PO_ETA'
  | 'CREATE_PO_FROM_EMAIL'
  | 'IMPORT_PROJECT_FROM_JETBUILT'
  | 'OPEN_QUOTE_RECONCILE'
  | 'PROCESS_INVOICE'
  | 'LOG_ACKNOWLEDGMENT'
  | 'DISMISS'

export interface ApiSuggestedAction {
  type: ApiActionType
  label: string
  description: string
  primary: boolean
  requiresPo: boolean
  params: Record<string, any>
  blockedReason: string | null
}

export interface ApiEmailContext {
  emailId: string
  extracted: ApiExtractedEmailData
  matchedPoId: string | null
  matchedPoNumber: string | null
  matchedProjectId: string | null
  matchedProjectCode: string | null
  attachments: Array<{
    id: string
    filename: string
    mimeType: string
    extractionId: string | null
    inferredType: ApiExtractedAttachmentHint['type']
  }>
  existingShipments: Array<{ id: string; trackingNumber: string }>
}

export interface ApiEmailActionsResponse {
  context: ApiEmailContext
  actions: ApiSuggestedAction[]
}

export interface ApiEmailRaw {
  id: string
  subject: string
  fromAddress: string
  fromName: string | null
  receivedAt: string
  bodyText: string | null
  attachments: Array<{
    id: string
    filename: string
    mimeType: string
    sizeBytes: number
  }>
}

export interface ApiEmailDispatchResult {
  action: string
  shipmentId?: string
  extractionId?: string
  poId?: string
  poNumber?: string
  attachmentId?: string
  expectedDelivery?: string
  classification?: string
  error?: string
  hint?: string
}

// ─── Project snapshots ────────────────────────────────────────────────────────

export type ApiSnapshotKind =
  | 'BOM_IMPORTED'
  | 'BOM_APPROVAL'
  | 'MANUAL'
  | 'CHANGE_ORDER_APPROVAL'
  | 'PROJECT_COMPLETION'

export interface ApiSnapshotListItem {
  id: string
  kind: ApiSnapshotKind
  label: string
  description: string | null
  createdAt: string
  bomVersionId: string | null
  createdBy: { id: string; name: string; email: string } | null
}

export interface ApiSnapshotListResponse {
  items: ApiSnapshotListItem[]
  total: number
  limit: number
  offset: number
}

export interface ApiBomLineSnapshot {
  id: string
  bomVersionId: string
  section: string
  type: string
  serviceType: string | null
  sortOrder: number
  room: string | null
  system: string | null
  description: string
  manufacturer: string | null
  model: string | null
  unitOfMeasure: string
  quantity: number
  orderedQty: number
  unitCost: number
  totalCost: number
  unitPrice: number
  totalPrice: number
  gpPercent: number
  gpDollar: number
  vwInclude: boolean
  inVectorworks: boolean
  vwPlacedQty: number
  vwDeviceNames: string[]
  vwDeviceName: string | null
  vwMake: string | null
  vwModel: string | null
  vwSyncedAt: string | null
  procurementStatus: string
  receivingStatus: string
  paidStatus: string
  isBudget: boolean
  budgetLineItemId: string | null
  originalUnitCost: number | null
  originalTotalCost: number | null
  originalUnitPrice: number | null
  originalTotalPrice: number | null
  replacementOriginalUnitPrice: number | null
  replacementOriginalTotalPrice: number | null
  deletedAt: string | null
  changeOrderId: string | null
  changeRequestId: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiSnapshotPayload {
  payloadVersion: number
  capturedAt: string
  bomVersionId: string | null
  bomLinesById: Record<string, ApiBomLineSnapshot>
  lineOrder: string[]
}

export interface ApiSnapshotDetail extends ApiSnapshotListItem {
  payload: ApiSnapshotPayload
  payloadVersion: number
}

export interface ApiSnapshotDiffEntryAdded {
  lineId: string
  status: 'added'
  line: ApiBomLineSnapshot
}
export interface ApiSnapshotDiffEntryRemoved {
  lineId: string
  status: 'removed'
  line: ApiBomLineSnapshot
}
export interface ApiSnapshotDiffEntryChanged {
  lineId: string
  status: 'changed'
  before: ApiBomLineSnapshot
  after: ApiBomLineSnapshot
  fieldDeltas: Record<string, { from: unknown; to: unknown }>
}

export interface ApiSnapshotDiffResponse {
  base: { id: string }
  compare: { id: string }
  diff: {
    added: ApiSnapshotDiffEntryAdded[]
    removed: ApiSnapshotDiffEntryRemoved[]
    changed: ApiSnapshotDiffEntryChanged[]
  }
}

export interface ApiBomLineHistoryEntry {
  snapshotId: string
  capturedAt: string
  kind: ApiSnapshotKind
  label: string
  description: string | null
  line: ApiBomLineSnapshot
}

export interface ApiBomLineHistoryResponse {
  lineId: string
  entries: ApiBomLineHistoryEntry[]
}

export interface ApiRevertResponse {
  reverted: string[]
  skipped: { lineId: string; reason: 'line-not-in-snapshot' | 'line-not-found-live' }[]
}
